import { useState, useEffect, useRef, useMemo } from "react";
import { trackConversion, newLeadEventId, getOpenAIRefs } from "@/lib/analytics";
import { talepKaynagi, TALEP_HATA_MESAJI } from "@/lib/talep_kaynagi";
import { toast } from "sonner";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import Layout from "@/components/Layout";
import VideoKanit from "@/components/VideoKanit";
import SEO from "@/components/SEO";
import { Link } from "wouter";
import OnuncuYilRozeti from "@/components/OnuncuYilRozeti";
import HeroGorsel from "@/components/HeroGorsel";
import { useRoas } from "@/contexts/RoasContext";
// canvas-confetti yalnizca kullanildiginda indirilir (ilk yukleme JS azalir)
const confetti = (opts: Record<string, unknown>) => {
  import("canvas-confetti").then(m => m.default(opts as any));
};
import { 
  TrendingUp, 
  Target, 
  ArrowRight, 
  CheckCircle2, 
  ChevronRight, 
  ShoppingBag, 
  Zap, 
  ShieldCheck, 
  Globe, 
  Phone, 
  User, 
  Mail, 
  Building2, 
  Sparkles,
  Minus,
  MessageSquare,
  DollarSign,
  Calendar,
  Clock,
  X,
  Award,
  BadgeCheck,
  BarChart3,
  Smartphone
} from "lucide-react";
import type { BlogPost } from "@/lib/mock_data";
import { Figur, YolHaritasi, HubSema, HuniSema } from "@/components/IcerikGorsel";

export default function Home() {
  // The userAuth hooks provides authentication state
  // To implement login/logout functionality, simply call logout() or redirect to getLoginUrl()
  let { user, loading, error, isAuthenticated, logout } = useAuth();

  const { roasData, updateRoasData } = useRoas();

  // Drag to scroll state & refs
  const sliderRef = useRef<HTMLDivElement>(null);
  const [isDown, setIsDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!sliderRef.current) return;
    setIsDown(true);
    setStartX(e.pageX - sliderRef.current.offsetLeft);
    setScrollLeft(sliderRef.current.scrollLeft);
  };

  const handleMouseLeave = () => {
    setIsDown(false);
  };

  const handleMouseUp = () => {
    setIsDown(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDown || !sliderRef.current) return;
    e.preventDefault();
    const x = e.pageX - sliderRef.current.offsetLeft;
    const walk = (x - startX) * 1.5; // Sürükleme hızı çarpanı
    sliderRef.current.scrollLeft = scrollLeft - walk;
    updateScrollProgress();
  };

  const updateScrollProgress = () => {
    if (!sliderRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
    const totalScroll = scrollWidth - clientWidth;
    if (totalScroll > 0) {
      const percentage = (scrollLeft / totalScroll) * 100;
      setScrollProgress(percentage);
    }
  };
  
  // Local state for modals
  const [isContactOpen, setIsOfferOpen] = useState(false);
  const [isCallOpen, setIsCallOpen] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isCallSubmitted, setIsFormCallSubmitted] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Form states
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientPlatform, setClientPlatform] = useState("ikas");
  const [clientMessage, setClientMessage] = useState("");

  // Feedback modal states
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [feedbackName, setFeedbackName] = useState("");
  const [feedbackCompany, setFeedbackCompany] = useState("");
  const [feedbackPlatform, setFeedbackPlatform] = useState("ikas");
  const [feedbackRoas, setFeedbackRoas] = useState("");
  const [feedbackText, setFeedbackText] = useState("");
  const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(false);

  // Auto-filled message for calculator
  const handleCalculatorCTA = () => {
    const formattedBudget = new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(roasData.budget);
    const formattedAov = new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(roasData.aov);
    
    setClientMessage(
      `Merhaba Dijipo! ROAS & Büyüme hesaplama aracınızda şu verileri hesapladım:\n• Aylık Reklam Bütçesi: ${formattedBudget}\n• Hedef ROAS Oranı: ${roasData.roas}x\n• Ortalama Sepet Tutarı: ${formattedAov}\n\nBu hedefler doğrultusunda e-ticaret sitemizi büyütmek için ücretsiz analiz almak istiyoruz.`
    );
    setIsOfferOpen(true);
  };

  // ROAS calculations
  const calculatedCiro = useMemo(() => {
    return roasData.budget * roasData.roas;
  }, [roasData.budget, roasData.roas]);

  const calculatedSiparis = useMemo(() => {
    return Math.round(calculatedCiro / roasData.aov);
  }, [calculatedCiro, roasData.aov]);

  const calculatedNetKazanc = useMemo(() => {
    return calculatedCiro - roasData.budget;
  }, [calculatedCiro, roasData.budget]);

  // Mobil Ciro Artış Hesaplayıcı State'leri
  const [currentMonthlyCiro, setCurrentMonthlyCiro] = useState(250000); // Varsayılan mevcut ciro: 250,000 TL
  const [currentCr, setCurrentCr] = useState(1.5); // Varsayılan mevcut dönüşüm oranı: %1.5
  
  // Mobil Uygulama Sonrası Tahmini Değerler (Dönüşüm Oranı genellikle 3 kat artar, biz muhafazakar olarak 2.5 kat alalım)
  const mobileCr = useMemo(() => {
    return Number((currentCr * 2.5).toFixed(2));
  }, [currentCr]);

  const estimatedMobileCiro = useMemo(() => {
    // Dönüşüm oranı 2.5 katına çıktığında ciro da aynı trafik seviyesinde 2.5 katına yaklaşır
    return Math.round(currentMonthlyCiro * 2.5);
  }, [currentMonthlyCiro]);

  const monthlyNetGain = useMemo(() => {
    return estimatedMobileCiro - currentMonthlyCiro;
  }, [estimatedMobileCiro, currentMonthlyCiro]);

  const yearlyNetGain = useMemo(() => {
    return monthlyNetGain * 12;
  }, [monthlyNetGain]);

  // Push Bildirim Simülasyonu State'leri
  const [activeNotification, setActiveNotification] = useState<{
    title: string;
    body: string;
    time: string;
    icon: string;
  } | null>(null);
  const [isNotificationSending, setIsNotificationSending] = useState(false);

  const notificationScenarios = [
    {
      id: "flash_sale",
      label: "⚡ Flaş İndirim",
      title: "🔥 BÜYÜK GECE İNDİRİMİ BAŞLADI!",
      body: "Sepetindeki ürünlerde sadece bu geceye özel ekstra %25 indirim tanımlandı. Kaçırmadan hemen satın al! 🛒",
      time: "Şimdi",
      icon: "⚡"
    },
    {
      id: "cart_abandon",
      label: "🛒 Sepet Hatırlatma",
      title: "😱 SEPETİNİZ SİZİ BEKLİYOR!",
      body: "Unuttuğun ürünler tükenmek üzere. Hemen uygulamaya dön ve siparişini tamamla, kargo bizden! 📦",
      time: "Şimdi",
      icon: "🛒"
    },
    {
      id: "personal_coupon",
      label: "🎁 Kişiye Özel Kupon",
      title: "🎉 SANA ÖZEL SÜRPRİZ HEDİYE!",
      body: "Sadece mobil uygulamaya özel 'MOBIL20' koduyla 200 TL indirim kazandın. Hemen kullan! 🌟",
      time: "Şimdi",
      icon: "🎁"
    }
  ];

  const triggerPushNotification = (scenario: typeof notificationScenarios[0]) => {
    if (isNotificationSending) return;
    setIsNotificationSending(true);
    setActiveNotification(null);

    // 1 saniye sonra ses ve titreşim hissiyle bildirimi ekranda göster
    setTimeout(() => {
      setActiveNotification(scenario);
      setIsNotificationSending(false);
      // Hafif konfeti patlatarak başarı hissi verelim
      confetti({
        particleCount: 30,
        spread: 40,
        origin: { y: 0.8 },
        colors: ["#9EFF00", "#000000"]
      });
    }, 1000);
  };

  // Fıstık yeşili ve marka renklerinde konfeti patlatma fonksiyonu
  const triggerConfetti = () => {
    const duration = 2 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 25, spread: 360, ticks: 50, zIndex: 100, colors: ["#9EFF00", "#000000", "#FFFFFF"] };

    const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

    const interval = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 40 * (timeLeft / duration);
      confetti(Object.assign({}, defaults, { particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } }));
      confetti(Object.assign({}, defaults, { particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } }));
    }, 250);
  };

  // tRPC form submission mutation
  const formSubmitMutation = trpc.form.submit.useMutation();

  // Form submission handlers
  const handleOfferSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formSubmitMutation.isPending) return;
    if (!clientName || !clientEmail) {
      alert("Lütfen isim ve e-posta alanlarını doldurun.");
      return;
    }

    // Piksel + Dönüşümler API'si tekilleştirmesi için ortak event id
    const eventId = newLeadEventId();
    const bitir = () => {
      setIsFormSubmitted(true);
      triggerConfetti();
      trackConversion("analysis", { platform: clientPlatform }, {
        email: clientEmail,
        phone: clientPhone || undefined,
        first_name: clientName.split(" ")[0],
        last_name: clientName.split(" ").slice(1).join(" "),
      }, eventId);
    };

    formSubmitMutation.mutate(
      {
        formType: "contact",
        name: clientName,
        email: clientEmail,
        phone: clientPhone || undefined,
        platform: clientPlatform || undefined,
        message: clientMessage || undefined,
        eventId,
        ...getOpenAIRefs(),
        kaynak: talepKaynagi("Ana sayfa · Ücretsiz analiz penceresi"),
      },
      {
        onSuccess: bitir,
        // Talep kaydedilemediyse başarı gösterilmez ve dönüşüm sayılmaz; ziyaretçi düzeltip tekrar dener
        onError: () => toast.error(TALEP_HATA_MESAJI),
      }
    );
  };

  const handleCallSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formSubmitMutation.isPending) return;
    if (!clientName || !clientPhone) {
      alert("Lütfen isim ve telefon alanlarını doldurun.");
      return;
    }

    const eventId = newLeadEventId();
    const bitir = () => {
      setIsFormCallSubmitted(true);
      triggerConfetti();
      trackConversion("callback", undefined, { phone: clientPhone, first_name: clientName.split(" ")[0] }, eventId);
    };

    formSubmitMutation.mutate(
      {
        formType: "callback",
        name: clientName,
        phone: clientPhone,
        eventId,
        ...getOpenAIRefs(),
        kaynak: talepKaynagi("Ana sayfa · Beni arayın penceresi"),
      },
      { onSuccess: bitir, onError: () => toast.error(TALEP_HATA_MESAJI) }
    );
  };

  // Blog verisi ana pakete girmesin diye dinamik yüklenir (74 yazının içeriği ~600KB)
  const [latestPosts, setLatestPosts] = useState<BlogPost[]>([]);
  useEffect(() => {
    import("@/lib/mock_data").then((m) => {
      setLatestPosts(m.BLOG_POSTS.slice(0, 3));
    });
  }, []);

  // Kurum ve yerel işletme şeması client/index.html'de tek kez tanımlı (#organization, #localbusiness);
  // ana sayfa ikinci, @id'siz bir işletme varlığı üretmez (P15).

  return (
    <Layout>
      <SEO 
        title="Dijipo | E-Ticaret Markaları İçin Reklam Ajansı"
        description="Dijipo, İstanbul merkezli e-ticaret reklam ajansı: Meta, Google ve TikTok reklam yönetimi, Shopify ve ikas mağaza kurulumu, SEO ve GEO ile ROAS odaklı büyüme."
        keywords="e-ticaret reklam ajansı, sosyal medya ajansı istanbul, instagram reklam yönetimi, shopify ajansı, ikas ajansı, dijital pazarlama ajansı, google ads ajansı, sosyal medya ajansı ankara, sosyal medya ajansı izmir, dijipo"
      />

      {/* HERO BANNER SECTION - LEFT TEXT + RIGHT E-COMMERCE IMAGES */}
      <section className="py-12 md:py-20 border-b-3 border-black bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_50%,rgba(158,255,0,0.08)_0,transparent_70%)] pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative">
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-4 items-center">
            {/* Left Side - Text & CTA */}
            <div className="space-y-6 md:space-y-8 relative z-10">
              <div className="flex items-center gap-4">
                <OnuncuYilRozeti boyut={88} />
                <div className="border-l-3 border-black pl-4">
                  <p className="font-mono text-[11px] font-bold tracking-widest text-neutral-500">2016'DAN BERİ DİJİPO</p>
                  <p className="font-black text-sm md:text-base leading-tight">Sektörde 10. yılımız,<br className="hidden md:block" /> 200+ e-ticaret projesi</p>
                </div>
              </div>
              <h1 className="font-black text-4xl md:text-6xl xl:text-7xl tracking-tight leading-[0.95] text-black uppercase">
                E-TİCARET <br />MARKALARINA <br />
                <span className="relative inline-block text-black">
                  SATIŞ ODAKLI REKLAM
                  <svg className="absolute left-0 -bottom-2 w-full h-3 text-[#9EFF00] fill-current" viewBox="0 0 100 10" preserveAspectRatio="none">
                    <path d="M0,5 Q25,10 50,5 T100,5" stroke="black" strokeWidth="2.5" fill="none" />
                  </svg>
                </span>
              </h1>

              <p className="text-sm md:text-lg text-neutral-600 font-bold leading-relaxed max-w-lg">
                Bir <Link href="/e-ticaret-reklam-ajansi" className="text-black underline decoration-[#9EFF00] decoration-4 underline-offset-2">e-ticaret reklam ajansı</Link> olarak
                markaların Google, Meta ve sosyal medya reklamlarını performans odaklı yönetiyoruz.
                Doğru hedef kitle, doğru reklam stratejisi ve ölçülebilir sonuçlarla e-ticaret
                satışlarınızı büyütmenize yardımcı oluyoruz.
              </p>

              {/* CTA Button */}
              <div className="py-2 flex justify-start">
                <button 
                  data-cta
                  onClick={() => setIsCalendarOpen(true)}
                  className="group flex items-center bg-white border-4 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:scale-[0.97] active:translate-x-[4px] active:translate-y-[4px] active:shadow-none transition-all cursor-pointer overflow-hidden"
                >
                  <div className="bg-black text-white p-4 border-r-4 border-black flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-[#9EFF00]" />
                  </div>
                  <div className="px-6 py-4 flex items-center gap-4">
                    <span className="font-black text-sm md:text-base uppercase tracking-wider text-black">TOPLANTI PLANLAYIN</span>
                    <div className="flex -space-x-2">
                      <img loading="lazy" decoding="async" src="/avatars/pazarlama-direktoru.svg" alt="Team 1" className="w-8 h-8 rounded-full border-2 border-black object-cover" />
                      <img loading="lazy" decoding="async" src="/avatars/kurucu.svg" alt="Team 2" className="w-8 h-8 rounded-full border-2 border-black object-cover" />
                    </div>
                  </div>
                </button>
              </div>

              <p className="text-xs md:text-sm font-mono font-bold text-neutral-500 uppercase tracking-wider">
                4 yıldır her ay 100.000 $+ reklam bütçesi yönetiyoruz. 100+ marka ile başarı hikayesi.
              </p>
            </div>

            {/* Right Side - marka stilinde performans kompozisyonu */}
            <div className="relative flex items-center justify-center lg:justify-end py-8 lg:py-4">
              <HeroGorsel />
            </div>
          </div>

        </div>

      </section>

      {/* GELİŞTİRMELERİMİZ — üst menüden kaldırıldı, buradan erişilir */}
      <section className="px-4 md:px-8 py-5 bg-black text-white border-b-3 border-black">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3 text-center md:text-left">
          <p className="text-sm font-bold">
            <span className="text-[#9EFF00] font-mono text-xs tracking-widest mr-2">YENİ</span>
            Shopify ve ikas için geliştirdiğimiz özel çözümler, tema modülleri ve otomasyonlar
          </p>
          <Link href="/gelistirmelerimiz" className="shrink-0 bg-[#9EFF00] text-black border-2 border-black px-4 py-2 font-bold text-xs shadow-[3px_3px_0px_0px_rgba(255,255,255,1)] hover:translate-x-[1px] hover:translate-y-[1px] transition-all">
            Geliştirmelerimizi İnceleyin →
          </Link>
        </div>
      </section>

      {/* HİZMET ALANLARI — hedef kelime kümeleri (iç bağlantı) */}
      <section className="cv-auto px-4 md:px-8 py-16 bg-white border-b-3 border-black">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="space-y-3">
            <span className="inline-block bg-[#9EFF00] text-black border-2 border-black px-3 py-1 font-mono font-bold text-xs uppercase shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">Hizmet Alanlarımız</span>
            <h2 className="font-black text-3xl md:text-5xl leading-none text-black uppercase">Aradığınız Uzmanlık Burada</h2>
            <p className="text-sm md:text-base text-neutral-600 font-semibold max-w-2xl">Yalnızca e-ticaret markalarıyla çalışıyoruz. İhtiyacınıza en yakın hizmet alanını seçin; her sayfada kapsam, süreç, fiyat bilgisi ve sık sorulan sorular var.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { href: "/e-ticaret-reklam-ajansi", baslik: "E-Ticaret Reklam Ajansı", metin: "Meta, Google ve TikTok'ta ROAS odaklı e-ticaret reklam yönetimi; Pixel + CAPI ölçümleme, katalog reklamları." },
              { href: "/google-ads-ajansi", baslik: "Google Ads Ajansı", metin: "Alışveriş, Performance Max ve arama kampanyaları; Merchant Center feed optimizasyonu, marj bazlı hedef ROAS." },
              { href: "/meta-reklam-ajansi", baslik: "Meta (Facebook & Instagram) Reklam Ajansı", metin: "Advantage+ Alışveriş, UGC kreatif, dinamik yeniden pazarlama; politika uyumlu, ölçeklenebilir kampanyalar." },
              { href: "/performans-pazarlama-ajansi", baslik: "Performans Pazarlama Ajansı", metin: "Her liranın ciro ve kâra dönüşünü ölçen kanal, kreatif ve dönüşüm optimizasyonu döngüsü." },
              { href: "/e-ticaret-danismanligi", baslik: "E-Ticaret Danışmanlığı", metin: "Altyapı seçimi (Shopify/ikas), fiyatlama, pazaryeri dengesi ve operasyon için 90 günlük yol haritası ve uygulama." },
              { href: "/ikas", baslik: "ikas Ajansı", metin: "ikas resmi iş ortağı: mağaza kurulumu, ikas'a geçiş, Meta & Google reklam yönetimi, CRO, SEO/GEO ve Fırsat pazaryeri tek ekipte." },
              { href: "/shopify", baslik: "Shopify Ajansı", metin: "Shopify Partner: kurulum, Shopify'a geçiş, reklam yönetimi, checkout dönüşümü ve Shopify Plus çözümleri." },
              { href: "/ikas-reklam-ajansi", baslik: "ikas Reklam Ajansı", metin: "ikas Pixel + CAPI, katalog ve Performance Max kampanyalarıyla ikas mağazalar için ROAS odaklı Meta ve Google reklam yönetimi." },
              { href: "/yapay-zeka-reklam-ajansi", baslik: "Yapay Zeka Reklam Ajansı", metin: "Advantage+, Performance Max ve Smart+ AI kampanyaları, üretken yapay zekayla kreatif üretimi ve ChatGPT reklamları; AI destekli ROAS yönetimi." },
              { href: "/e-ticaret-altyapi-gecisi", baslik: "E-Ticaret Altyapı Geçişi", metin: "Ticimax, İdeasoft, T-Soft, WooCommerce ve diğer altyapılardan ikas veya Shopify'a planlı taşıma; veri aktarımı, SEO kaybını en aza indiren 301 yönlendirme, entegrasyon." },
              { href: "/tiktok-reklam-ajansi", baslik: "TikTok Reklam Ajansı", metin: "Smart+, Spark Ads, TikTok Pixel + Events API, UGC ve AI video kreatiflerle e-ticaret için ROAS odaklı TikTok reklam yönetimi." },
            ].map(k => (
              <Link key={k.href} href={k.href} className="group block bg-white border-4 border-black p-6 shadow-[5px_5px_0px_0px_rgba(0,0,0,1)] hover:shadow-[7px_7px_0px_0px_rgba(158,255,0,1)] hover:-translate-y-0.5 transition-all">
                <h3 className="font-black text-lg text-black leading-tight">{k.baslik}</h3>
                <p className="text-xs text-neutral-600 font-semibold leading-relaxed mt-2">{k.metin}</p>
                <span className="inline-block mt-4 font-mono font-bold text-xs uppercase tracking-wider border-b-2 border-[#9EFF00] group-hover:border-black transition-colors">Detayları gör →</span>
              </Link>
            ))}
          </div>

          {/* Şehirler — şehir sayfalarına bağlam içi bağlantı (her şehir için tek sayfa) */}
          <div className="border-t-3 border-black pt-8 space-y-4">
            <h3 className="font-black text-xl md:text-2xl text-black">Şehrinizde E-Ticaret Reklam Ajansı</h3>
            <p className="text-sm text-neutral-600 font-semibold max-w-3xl">Ofisimiz İstanbul Ataşehir'de; Türkiye'nin her yerindeki markalarla çevrim içi çalışıyoruz. Şehrinizin pazar dinamiklerini ve öne çıkan sektörlerini anlattığımız sayfalar:</p>
            <div className="flex flex-wrap gap-2">
              {[
                // Şehir verisinin tamamı (sehir_data.ts) ana sayfa paketine girmesin diye yalnızca bağlantılar
                { href: "/istanbul-eticaret-reklam-ajansi", etiket: "İstanbul" },
                { href: "/izmir-eticaret-reklam-ajansi", etiket: "İzmir" },
                { href: "/ankara-eticaret-reklam-ajansi", etiket: "Ankara" },
                { href: "/antalya-eticaret-reklam-ajansi", etiket: "Antalya" },
                { href: "/bursa-eticaret-reklam-ajansi", etiket: "Bursa" },
              ].map(l => (
                <Link key={l.href} href={l.href} className="border-2 border-black bg-white px-3 py-1.5 text-xs font-bold hover:bg-[#9EFF00] transition-colors">{l.etiket} e-ticaret reklam ajansı</Link>
              ))}
            </div>
          </div>

          {/* Çalıştığımız altyapılar — her altyapıda reklam yönetimi, ikas/Shopify'a geçiş */}
          <div className="border-t-3 border-black pt-8 space-y-4">
            <h3 className="font-black text-xl md:text-2xl text-black">Türkiye'deki Tüm E-Ticaret Altyapılarıyla Çalışıyoruz</h3>
            <p className="text-sm text-neutral-600 font-semibold max-w-3xl">Shopify ve ikas resmi iş ortağıyız; ancak reklam yönetimi altyapıdan bağımsızdır. Ticimax, İdeasoft, T-Soft, PlatinMarket, WooCommerce, OpenCart, Wix, Magento ve PrestaShop üzerinde çalışan markaların reklamlarını yönetiyor, büyümek isteyenleri ikas veya Shopify'a SEO kaybını en aza indirerek taşıyoruz.</p>
            <div className="flex flex-wrap gap-2">
              {[
                ["/e-ticaret-altyapi-gecisi", "Ticimax"],
                ["/e-ticaret-altyapi-gecisi", "İdeasoft"],
                ["/e-ticaret-altyapi-gecisi", "T-Soft"],
                ["/e-ticaret-altyapi-gecisi", "PlatinMarket"],
                ["/e-ticaret-altyapi-gecisi", "WooCommerce"],
                ["/e-ticaret-altyapi-gecisi", "OpenCart"],
                ["/e-ticaret-altyapi-gecisi", "Wix"],
                ["/e-ticaret-altyapi-gecisi", "Magento"],
                ["/e-ticaret-altyapi-gecisi", "PrestaShop"],
                ["/shopify", "Shopify"],
                ["/ikas", "ikas"],
              ].map(([href, ad]) => (
                <Link key={ad} href={href} className="border-2 border-black bg-white px-3 py-1.5 text-xs font-bold hover:bg-[#9EFF00] transition-colors">{ad}</Link>
              ))}
              <Link href="/e-ticaret-altyapi-gecisi" className="border-2 border-black bg-black text-[#9EFF00] px-3 py-1.5 text-xs font-bold hover:bg-neutral-800 transition-colors">Altyapı geçişi →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* BÜYÜME MODELİ — vektörel çizimler (nasıl çalışıyoruz, hizmet ekosistemi, satış hunisi) */}
      <section className="cv-auto px-4 md:px-8 py-16 bg-neutral-50 border-b-3 border-black">
        <div className="max-w-6xl mx-auto space-y-8">
          <div className="space-y-3">
            <span className="inline-block bg-black text-[#9EFF00] border-2 border-black px-3 py-1 font-mono font-bold text-xs uppercase shadow-[2px_2px_0px_0px_rgba(158,255,0,1)]">Büyüme Modeli</span>
            <h2 className="font-black text-3xl md:text-5xl leading-none text-black uppercase">Nasıl Çalışıyoruz?</h2>
            <p className="text-sm md:text-base text-neutral-600 font-semibold max-w-2xl">Her markada aynı dört adım: ücretsiz hesap analizi, ölçümleme kurulumu, kreatif ve kitle testi, kârlı ROAS'ta ölçekleme. Hizmetler birbirini tamamlar; huninin her katmanı ayrı ölçülür.</p>
          </div>
          <Figur no={1} etiket="Dört adımlı çalışma modeli">
            <YolHaritasi basliklar={["Ücretsiz hesap analizi: reklam hesapları ve ölçümleme 48 saatte denetlenir", "Ölçümleme kurulumu: Pixel + CAPI, GA4 ve ürün feed'i", "Test ve öğrenme: 6-10 kreatif konsepti 2-4 haftada küçük bütçeyle", "Ölçekleme: kârlı ROAS'ta kademeli bütçe artışı ve aylık rapor"]} />
          </Figur>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <Figur no={2} etiket="Hizmet ekosistemi">
              <HubSema merkez="E-ticaret markanızın büyüme ekibi" dallar={["Meta, Google ve TikTok reklam yönetimi", "ikas ve Shopify mağaza kurulumu ve altyapı geçişi", "SEO ve GEO: Google ve yapay zeka aramalarında görünürlük", "UGC ve kreatif üretimi", "E-posta, CRM ve pazarlama otomasyonu", "Pazaryeri yönetimi: Trendyol, Hepsiburada, Amazon, Fırsat"]} />
            </Figur>
            <Figur no={3} etiket="E-ticaret satış hunisi">
              <HuniSema katmanlar={["Keşif: geniş hedefleme, video ve UGC kreatifleriyle yeni kitle", "Değerlendirme: katalog reklamları, ürün sayfası ve sosyal kanıt", "Satın alma: sepet terk yeniden pazarlaması ve ödeme adımı optimizasyonu", "Tekrar satın alma: e-posta, SMS ve sadakat akışlarıyla LTV artışı"]} />
            </Figur>
          </div>
        </div>
      </section>

      {/* WHY US / METHODOLOGY SECTION */}
      <section className="cv-auto py-20 md:py-28 px-4 md:px-8 border-b-3 border-black bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-5 space-y-6">
              <div className="inline-block bg-[#9EFF00] text-black font-mono text-xs px-3 py-1 border-2 border-black uppercase font-bold shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
                BÜYÜME METODOLOJİMİZ
              </div>
              <h2 className="font-black text-3xl md:text-5xl leading-none text-black uppercase">
                CİRONUZU ARTIRMAK BİR TESADÜF DEĞİL, <br />
                <span className="bg-black text-white px-2 border-2 border-black inline-block my-1 shadow-[4px_4px_0px_0px_rgba(158,255,0,1)]">MÜHENDİSLİKTİR</span>
              </h2>
              <p className="text-sm md:text-base text-neutral-600 font-bold leading-relaxed">
                Geleneksel ajanslar gibi sadece "reklam kurup bırakmıyoruz." Dijipo olarak e-ticaret sitenizin dönüşüm oranını, sunucu hızını, sepet tutarını ve reklam piksellerini bütünsel bir mühendislik yaklaşımıyla optimize ediyoruz.
              </p>
              
              <div className="space-y-4 pt-2">
                <div className="flex items-start space-x-3">
                  <div className="bg-[#9EFF00] border-2 border-black p-1 shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] shrink-0 mt-0.5">
                    <CheckCircle2 className="h-4 w-4 text-black" />
                  </div>
                  <div>
                    <h3 className="font-black text-sm uppercase text-black">Shopify & İkas Resmi İş Ortağı</h3>
                    <p className="text-xs text-neutral-500 font-semibold">Sadece en hızlı ve dönüşümü en yüksek iki altyapıda uzmanlaştık.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="bg-[#9EFF00] border-2 border-black p-1 shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] shrink-0 mt-0.5">
                    <CheckCircle2 className="h-4 w-4 text-black" />
                  </div>
                  <div>
                    <h3 className="font-black text-sm uppercase text-black">Yapay Zeka ve GEO Entegrasyonu</h3>
                    <p className="text-xs text-neutral-500 font-semibold">Sitenizi sadece Google için değil, ChatGPT ve Claude asistanları için de optimize ediyoruz.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white border-3 border-black p-6 shadow-[5px_5px_0px_0px_rgba(0,0,0,1)] space-y-4">
                <div className="w-10 h-10 bg-black text-[#9EFF00] border-2 border-black flex items-center justify-center font-black shadow-[2px_2px_0px_0px_rgba(158,255,0,1)]">
                  01
                </div>
                <h3 className="font-black text-lg uppercase text-black">Dönüşüm Oranı Optimizasyonu</h3>
                <p className="text-xs text-neutral-500 font-semibold leading-relaxed">
                  Sitenize gelen trafiğin müşteriye dönüşme oranını (CR) artırmak için sepet adımları, ödeme sayfaları ve ürün açıklamalarını baştan tasarlıyoruz.
                </p>
              </div>

              <div className="bg-white border-3 border-black p-6 shadow-[5px_5px_0px_0px_rgba(0,0,0,1)] space-y-4">
                <div className="w-10 h-10 bg-[#9EFF00] text-black border-2 border-black flex items-center justify-center font-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
                  02
                </div>
                <h3 className="font-black text-lg uppercase text-black">Performans Pazarlaması</h3>
                <p className="text-xs text-neutral-500 font-semibold leading-relaxed">
                  Meta (Facebook/Instagram), Google ve TikTok reklam bütçelerinizi ROAS (Reklam Getirisi) odaklı yönetiyor, her kuruşun karşılığını ciro olarak raporluyoruz.
                </p>
              </div>

              <div className="bg-white border-3 border-black p-6 shadow-[5px_5px_0px_0px_rgba(0,0,0,1)] space-y-4">
                <div className="w-10 h-10 bg-black text-[#9EFF00] border-2 border-black flex items-center justify-center font-black shadow-[2px_2px_0px_0px_rgba(158,255,0,1)]">
                  03
                </div>
                <h3 className="font-black text-lg uppercase text-black">Pazarlama Otomasyonu</h3>
                <p className="text-xs text-neutral-500 font-semibold leading-relaxed">
                  Terk edilen sepetler, hoş geldin serileri ve sadık müşteri senaryoları için gelişmiş e-posta/SMS otomasyon akışları kurarak cirolarınızı otomatik olarak artırıyoruz.
                </p>
              </div>

              <div className="bg-white border-3 border-black p-6 shadow-[5px_5px_0px_0px_rgba(0,0,0,1)] space-y-4">
                <div className="w-10 h-10 bg-[#9EFF00] text-black border-2 border-black flex items-center justify-center font-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
                  04
                </div>
                <h3 className="font-black text-lg uppercase text-black">UGC & İçerik Yönetimi</h3>
                <p className="text-xs text-neutral-500 font-semibold leading-relaxed">
                  Reklamlarınızda en yüksek tıklama oranına sahip doğal kullanıcı içerikleri (UGC) üretiyor, influencer iş birlikleriyle sosyal kanıt oluşturuyoruz.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ROAS & BÜYÜME HESAPLAMA ARACI (INTERACTIVE WIDGET) */}
      <section className="cv-auto py-20 md:py-28 px-4 md:px-8 border-b-3 border-black bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white border-4 border-black p-6 md:p-10 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
            
            <div className="text-center max-w-2xl mx-auto mb-12 space-y-3">
              <div className="inline-block bg-[#9EFF00] text-black font-mono text-xs px-2.5 py-1 border-2 border-black uppercase font-bold">
                İNTERAKTİF BÜYÜME PANELİ
              </div>
              <h2 className="font-black text-2xl md:text-4xl text-black uppercase">
                REKLAM BÜTÇENİZİN <br />
                <span className="bg-black text-[#9EFF00] px-2 border-2 border-black inline-block my-1 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">GETİRİSİNİ HESAPLAYIN</span>
              </h2>
              <p className="text-xs md:text-sm text-neutral-500 font-bold">
                Aylık reklam bütçenizi ve hedeflediğiniz ROAS oranını seçerek elde edeceğiniz tahmini ciroyu anlık olarak görün.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              
              {/* Sliders Column */}
              <div className="lg:col-span-7 space-y-8 border-b-2 lg:border-b-0 lg:border-r-2 border-black pb-8 lg:pb-0 lg:pr-8">
                
                {/* Budget Slider */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center font-mono font-bold text-xs md:text-sm">
                    <span className="text-black uppercase">AYLIK REKLAM BÜTÇESİ</span>
                    <span className="text-lg text-black bg-[#9EFF00] px-2 py-0.5 border-2 border-black shadow-[1.5px_1.5px_0px_0px_rgba(0,0,0,1)]">
                      {new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(roasData.budget)}
                    </span>
                  </div>
                  <input
                    aria-label="Aylık reklam bütçesi"
                    type="range" 
                    min="5000" 
                    max="500000" 
                    step="5000"
                    value={roasData.budget}
                    onChange={(e) => updateRoasData({ budget: Number(e.target.value) })}
                    className="w-full h-3 bg-neutral-200 border-2 border-black rounded-none appearance-none cursor-pointer accent-black"
                  />
                  <div className="flex justify-between text-[10px] font-mono font-bold text-neutral-600">
                    <span>₺5.000</span>
                    <span>₺250.000</span>
                    <span>₺500.000</span>
                  </div>
                </div>

                {/* ROAS Slider */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center font-mono font-bold text-xs md:text-sm">
                    <span className="text-black uppercase">HEDEF ROAS (REKLAM GETİRİSİ)</span>
                    <span className="text-lg text-black bg-[#9EFF00] px-2 py-0.5 border-2 border-black shadow-[1.5px_1.5px_0px_0px_rgba(0,0,0,1)]">
                      {roasData.roas.toFixed(1)}x
                    </span>
                  </div>
                  <input
                    aria-label="Hedef ROAS (reklam getirisi)"
                    type="range" 
                    min="1" 
                    max="15" 
                    step="0.5"
                    value={roasData.roas}
                    onChange={(e) => updateRoasData({ roas: Number(e.target.value) })}
                    className="w-full h-3 bg-neutral-200 border-2 border-black rounded-none appearance-none cursor-pointer accent-black"
                  />
                  <div className="flex justify-between text-[10px] font-mono font-bold text-neutral-600">
                    <span>1.0x</span>
                    <span>7.5x</span>
                    <span>15.0x</span>
                  </div>
                </div>

                {/* AOV Slider */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center font-mono font-bold text-xs md:text-sm">
                    <span className="text-black uppercase">ORTALAMA SEPET TUTARI (AOV)</span>
                    <span className="text-lg text-black bg-[#9EFF00] px-2 py-0.5 border-2 border-black shadow-[1.5px_1.5px_0px_0px_rgba(0,0,0,1)]">
                      {new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(roasData.aov)}
                    </span>
                  </div>
                  <input
                    aria-label="Ortalama sepet tutarı"
                    type="range" 
                    min="100" 
                    max="5000" 
                    step="50"
                    value={roasData.aov}
                    onChange={(e) => updateRoasData({ aov: Number(e.target.value) })}
                    className="w-full h-3 bg-neutral-200 border-2 border-black rounded-none appearance-none cursor-pointer accent-black"
                  />
                  <div className="flex justify-between text-[10px] font-mono font-bold text-neutral-600">
                    <span>₺100</span>
                    <span>₺2.500</span>
                    <span>₺5.000</span>
                  </div>
                </div>

              </div>

              {/* Results Column */}
              <div className="lg:col-span-5 space-y-6">
                <h3 className="font-mono font-black text-sm text-black uppercase tracking-wider">TAHMİNİ AYLIK BÜYÜME RAPORU</h3>
                
                <div className="space-y-4 font-mono">
                  
                  <div className="bg-black text-white p-4 border-2 border-black shadow-[3px_3px_0px_0px_rgba(158,255,0,1)]">
                    <span className="text-[10px] font-bold text-neutral-400 uppercase block">TAHMİNİ AYLIK CİRO</span>
                    <span className="text-2xl md:text-3xl font-black text-[#9EFF00]">
                      {new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(calculatedCiro)}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-100 p-3 border-2 border-black">
                      <span className="text-[9px] font-bold text-neutral-600 uppercase block">AYLIK SİPARİŞ</span>
                      <span className="text-lg font-black text-black">{calculatedSiparis.toLocaleString("tr-TR")}</span>
                    </div>
                    <div className="bg-gray-100 p-3 border-2 border-black">
                      <span className="text-[9px] font-bold text-neutral-600 uppercase block">NET KAZANÇ (CİRO-BÜTÇE)</span>
                      <span className="text-lg font-black text-[#1e7e34]">
                        +{new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(calculatedNetKazanc)}
                      </span>
                    </div>
                  </div>

                </div>

                <div className="pt-2">
                  <button 
                    onClick={handleCalculatorCTA}
                    className="w-full bg-[#9EFF00] text-black border-3 border-black py-4 font-black text-xs md:text-sm uppercase tracking-wider shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-all cursor-pointer flex items-center justify-center gap-2"
                  >
                    Bu Büyümeyi Birlikte Gerçekleştirelim <ArrowRight className="w-5 h-5" />
                  </button>
                </div>

              </div>

            </div>

          </div>
        </div>
      </section>

      {/* MÜŞTERİ VİDEOLARI — YouTube başarı hikayeleri (tıklayınca yüklenir) */}
      <section className="cv-auto py-16 md:py-24 px-4 md:px-8 border-b-3 border-black bg-neutral-50">
        <div className="max-w-7xl mx-auto">
          <VideoKanit kompakt />
        </div>
      </section>

      {/* SUCCESS CASE STUDIES / TESTIMONIALS SECTION - WITH INTERACTIVE SLIDER & GREEN DETAILS */}
      <section className="cv-auto py-20 md:py-28 px-4 md:px-8 border-b-3 border-black bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto">
          
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div className="max-w-3xl space-y-4">
              <div className="inline-block bg-[#9EFF00] text-black font-mono text-xs px-3 py-1 border-2 border-black uppercase font-bold shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
                MUTLU E-TİCARET DEVLERİ
              </div>
              <h2 className="font-black text-3xl md:text-5xl leading-none text-black uppercase">
                SHOPIFY & İKAS KULLANAN <br />
                <span className="bg-black text-white px-2 border-2 border-black inline-block my-1 shadow-[4px_4px_0px_0px_rgba(158,255,0,1)]">MARKALARIMIZIN YORUMLARI</span>
              </h2>
              <p className="text-sm md:text-base text-neutral-600 font-bold">
                Altyapı hızı ve Dijipo'nun dönüşüm mühendisliği ile rekor kıran bazı başarılı iş ortaklarımız.
              </p>
            </div>
            
            {/* Slider Navigation Buttons */}
            <div className="flex items-center space-x-4 shrink-0">
              <button 
                onClick={() => {
                  const slider = document.getElementById('testimonials-slider');
                  if (slider) slider.scrollBy({ left: -350, behavior: 'smooth' });
                }}
                className="w-12 h-12 bg-white text-black border-3 border-black flex items-center justify-center font-black text-lg shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-all cursor-pointer"
              >
                ←
              </button>
              <button 
                onClick={() => {
                  const slider = document.getElementById('testimonials-slider');
                  if (slider) slider.scrollBy({ left: 350, behavior: 'smooth' });
                }}
                className="w-12 h-12 bg-[#9EFF00] text-black border-3 border-black flex items-center justify-center font-black text-lg shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-all cursor-pointer"
              >
                →
              </button>
            </div>
          </div>

          {/* Testimonials Horizontal Scrollable Area */}
          <div 
            id="testimonials-slider"
            ref={sliderRef}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            onScroll={updateScrollProgress}
            className={`flex overflow-x-auto gap-8 pt-6 pb-8 scrollbar-none snap-x snap-mandatory ${isDown ? 'cursor-grabbing select-none' : 'cursor-grab'}`}
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            
            {/* Testimonial 1 */}
            <div className="min-w-[280px] sm:min-w-[350px] md:min-w-[400px] bg-white border-3 border-black p-8 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-2.5 hover:-translate-x-2 hover:scale-[1.02] hover:shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] transition-all duration-300 ease-out flex flex-col justify-between relative snap-start group">
              <div className="absolute -top-4 -right-4 bg-[#9EFF00] text-black font-mono font-bold text-xs px-2.5 py-1 border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] group-hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all duration-300">
                6.4x ROAS / İKAS
              </div>
              <div className="space-y-4">
                <span className="text-5xl text-[#9EFF00] font-black leading-none block transition-all duration-300 ease-out origin-left group-hover:-rotate-12 group-hover:scale-110 group-hover:text-black">“</span>
                <p className="text-xs md:text-sm text-neutral-600 font-bold leading-relaxed">
                  "ikas altyapısına geçiş yaptıktan sonra Dijipo ile reklam yönetimine başladık. Sunucu hızımızın artması ve Dijipo'nun sepet adımları optimizasyonu sayesinde dönüşüm oranımız %1.2'den %3.8'e yükseldi. Reklam harcamalarımızın geri dönüşü (ROAS) ise rekor seviyeye ulaştı."
                </p>
              </div>
              <div className="border-t-2 border-black pt-4 mt-6 flex items-center space-x-3">
                <div className="w-10 h-10 bg-neutral-100 border-2 border-black rounded-full overflow-hidden shrink-0">
                  <img loading="lazy" decoding="async" src="/avatars/musteri-1.svg" alt="Ahmet S." className="w-full h-full object-cover" />
                </div>
                <div>
                  <h3 className="font-black text-xs uppercase text-black">Ahmet S.</h3>
                  <p className="text-[10px] text-neutral-600 font-bold font-mono">Kurucu, Süvari E-Ticaret</p>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="min-w-[280px] sm:min-w-[350px] md:min-w-[400px] bg-white border-3 border-black p-8 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-2.5 hover:-translate-x-2 hover:scale-[1.02] hover:shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] transition-all duration-300 ease-out flex flex-col justify-between relative snap-start group">
              <div className="absolute -top-4 -right-4 bg-[#9EFF00] text-black font-mono font-bold text-xs px-2.5 py-1 border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] group-hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all duration-300">
                5.2x ROAS / SHOPIFY
              </div>
              <div className="space-y-4">
                <span className="text-5xl text-[#9EFF00] font-black leading-none block transition-all duration-300 ease-out origin-left group-hover:-rotate-12 group-hover:scale-110 group-hover:text-black">“</span>
                <p className="text-xs md:text-sm text-neutral-600 font-bold leading-relaxed">
                  "Shopify Plus mağazamızın küresel reklamlarını Dijipo yönetiyor. Özellikle UGC içerik üretim ekibinin hazırladığı doğal videolar, reklam tıklama oranlarımızı (CTR) ikiye katladı. Yurt dışı satışlarımız ilk 3 ayda %240 büyüme gösterdi."
                </p>
              </div>
              <div className="border-t-2 border-black pt-4 mt-6 flex items-center space-x-3">
                <div className="w-10 h-10 bg-neutral-100 border-2 border-black rounded-full overflow-hidden shrink-0">
                  <img loading="lazy" decoding="async" src="/avatars/musteri-2.svg" alt="Merve K." className="w-full h-full object-cover" />
                </div>
                <div>
                  <h3 className="font-black text-xs uppercase text-black">Merve K.</h3>
                  <p className="text-[10px] text-neutral-600 font-bold font-mono">Pazarlama Direktörü, Mehry Mu</p>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="min-w-[280px] sm:min-w-[350px] md:min-w-[400px] bg-white border-3 border-black p-8 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-2.5 hover:-translate-x-2 hover:scale-[1.02] hover:shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] transition-all duration-300 ease-out flex flex-col justify-between relative snap-start group">
              <div className="absolute -top-4 -right-4 bg-[#9EFF00] text-black font-mono font-bold text-xs px-2.5 py-1 border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] group-hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all duration-300">
                8.1x ROAS / İKAS
              </div>
              <div className="space-y-4">
                <span className="text-5xl text-[#9EFF00] font-black leading-none block transition-all duration-300 ease-out origin-left group-hover:-rotate-12 group-hover:scale-110 group-hover:text-black">“</span>
                <p className="text-xs md:text-sm text-neutral-600 font-bold leading-relaxed">
                  "Pazarlama otomasyonu ve CRM akışları konusundaki uzmanlıkları sayesinde, terk edilen sepet cirolarımızı %300 oranında geri kazandık. Dijipo sadece reklam bütçemizi yönetmekle kalmıyor, sitemizin ciro getiren tüm kılcal damarlarını optimize ediyor."
                </p>
              </div>
              <div className="border-t-2 border-black pt-4 mt-6 flex items-center space-x-3">
                <div className="w-10 h-10 bg-neutral-100 border-2 border-black rounded-full overflow-hidden shrink-0">
                  <img loading="lazy" decoding="async" src="/avatars/musteri-3.svg" alt="Caner T." className="w-full h-full object-cover" />
                </div>
                <div>
                  <h3 className="font-black text-xs uppercase text-black">Caner T.</h3>
                  <p className="text-[10px] text-neutral-600 font-bold font-mono">E-Ticaret Müdürü, Adore Oyuncak</p>
                </div>
              </div>
            </div>

            {/* Testimonial 4 */}
            <div className="min-w-[280px] sm:min-w-[350px] md:min-w-[400px] bg-white border-3 border-black p-8 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-2.5 hover:-translate-x-2 hover:scale-[1.02] hover:shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] transition-all duration-300 ease-out flex flex-col justify-between relative snap-start group">
              <div className="absolute -top-4 -right-4 bg-[#9EFF00] text-black font-mono font-bold text-xs px-2.5 py-1 border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] group-hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all duration-300">
                4.9x ROAS / SHOPIFY
              </div>
              <div className="space-y-4">
                <span className="text-5xl text-[#9EFF00] font-black leading-none block transition-all duration-300 ease-out origin-left group-hover:-rotate-12 group-hover:scale-110 group-hover:text-black">“</span>
                <p className="text-xs md:text-sm text-neutral-600 font-bold leading-relaxed">
                  "Sıfırdan kurduğumuz Shopify mağazamızın e-ihracat süreçlerini Dijipo ile kurguladık. Özellikle yerel ödeme yöntemleri entegrasyonu ve ülkelere özel reklam kampanyaları sayesinde, yurt dışı ciromuz yerel ciromuzu geride bıraktı."
                </p>
              </div>
              <div className="border-t-2 border-black pt-4 mt-6 flex items-center space-x-3">
                <div className="w-10 h-10 bg-neutral-100 border-2 border-black rounded-full overflow-hidden shrink-0">
                  <img loading="lazy" decoding="async" src="/avatars/musteri-4.svg" alt="Elif B." className="w-full h-full object-cover" />
                </div>
                <div>
                  <h3 className="font-black text-xs uppercase text-black">Elif B.</h3>
                  <p className="text-[10px] text-neutral-600 font-bold font-mono">E-Ticaret Müdürü, Alwaysfashion</p>
                </div>
              </div>
            </div>

          </div>

          {/* Neo-Brutalist Scroll Progress Bar */}
          <div className="w-full max-w-md mx-auto mt-6 bg-white border-3 border-black h-5 relative shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] overflow-hidden">
            <div 
              className="h-full bg-[#9EFF00] border-r-3 border-black transition-all duration-100 ease-out"
              style={{ width: `${scrollProgress}%` }}
            />
          </div>

        </div>
      </section>

      {/* ADSMANAGER REKLAM HESABI KİRALAMA SECTION */}
      <section className="cv-auto py-20 md:py-28 px-4 md:px-8 border-b-3 border-black bg-[#9EFF00] text-black relative overflow-hidden">
        {/* Decorative Grid Pattern */}
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, black 1px, transparent 0)', backgroundSize: '20px 20px' }}></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-block bg-black text-[#9EFF00] font-mono text-xs px-3 py-1 border-2 border-black uppercase font-bold shadow-[2px_2px_0px_0px_rgba(255,255,255,1)]">
                ÖZEL REKLAM ALTYAPISI
              </div>
              <h2 className="font-black text-3xl md:text-5xl leading-none uppercase">
                BÜTÇE LİMİTLERİNE TAKILMAYIN: <br />
                <span className="bg-black text-white px-2 border-2 border-black inline-block my-1 shadow-[4px_4px_0px_0px_rgba(255,255,255,1)]">AdsManager HİZMETİ</span>
              </h2>
              <p className="text-sm md:text-base text-neutral-800 font-bold leading-relaxed max-w-2xl">
                Facebook ve Instagram reklamlarında harcama limitleri, hesap kapanmaları veya piksel sorunlarıyla vakit kaybetmeyin. Dijipo AdsManager ile yüksek harcama limitli, onaylı ve ajans garantili reklam hesaplarını kiralayın, kesintisiz ölçeklenmeye başlayın.
              </p>
              
              {/* Features Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="bg-white border-2 border-black p-4 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] flex items-start space-x-3">
                  <div className="bg-[#9EFF00] border-2 border-black p-1 text-black font-black text-xs shrink-0">✓</div>
                  <div>
                    <h3 className="font-black text-xs uppercase">Sınırsız Günlük Harcama</h3>
                    <p className="text-[10px] text-neutral-500 font-bold">İlk günden itibaren bütçe limitlerine takılmadan yüksek harcama yapın.</p>
                  </div>
                </div>
                <div className="bg-white border-2 border-black p-4 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] flex items-start space-x-3">
                  <div className="bg-[#9EFF00] border-2 border-black p-1 text-black font-black text-xs shrink-0">✓</div>
                  <div>
                    <h3 className="font-black text-xs uppercase">7/24 Teknik Destek</h3>
                    <p className="text-[10px] text-neutral-500 font-bold">Olası askıya alınma veya politika ihlali durumlarında anında yeni hesap.</p>
                  </div>
                </div>
                <div className="bg-white border-2 border-black p-4 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] flex items-start space-x-3">
                  <div className="bg-[#9EFF00] border-2 border-black p-1 text-black font-black text-xs shrink-0">✓</div>
                  <div>
                    <h3 className="font-black text-xs uppercase">Gelişmiş Piksel & Veri</h3>
                    <p className="text-[10px] text-neutral-500 font-bold">iOS 14+ sonrası en yüksek veri doğruluğu sağlayan CAPI entegrasyonu.</p>
                  </div>
                </div>
                <div className="bg-white border-2 border-black p-4 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] flex items-start space-x-3">
                  <div className="bg-[#9EFF00] border-2 border-black p-1 text-black font-black text-xs shrink-0">✓</div>
                  <div>
                    <h3 className="font-black text-xs uppercase">Hızlı Kurulum</h3>
                    <p className="text-[10px] text-neutral-500 font-bold">Evrak işleriyle uğraşmadan, 2 saat içinde aktif reklam yayını.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 space-y-6">
              <div className="bg-white border-4 border-black p-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] relative">
                <div className="absolute -top-4 -right-4 bg-black text-[#9EFF00] font-mono font-bold text-[10px] px-2.5 py-1 border-2 border-black shadow-[2px_2px_0px_0px_rgba(158,255,0,1)]">
                  POPÜLER SEÇENEK
                </div>
                
                <h3 className="font-black text-xl uppercase mb-2">KURUMSAL AdsManager</h3>
                <p className="text-xs text-neutral-500 font-bold mb-6">Özellikle Shopify ve İkas altyapılı, günlük +$1,000 harcama hedefleyen e-ticaret markaları için.</p>
                
                <div className="border-t-2 border-b-2 border-black py-4 my-4 space-y-2">
                  <div className="flex justify-between text-xs font-bold">
                    <span>MİNİMUM BÜTÇE:</span>
                    <span className="font-mono">Sınırsız</span>
                  </div>
                  <div className="flex justify-between text-xs font-bold">
                    <span>KOMİSYON ORANI:</span>
                    <span className="font-mono text-[#1e7e34]">%3'ten Başlayan</span>
                  </div>
                  <div className="flex justify-between text-xs font-bold">
                    <span>AKTİVASYON SÜRESİ:</span>
                    <span className="font-mono">Aynı Gün (2 Saat)</span>
                  </div>
                </div>

                <div className="pt-4">
                  <Link href="/facebook-reklam-hesabi-kiralama">
                    <button className="w-full bg-black text-[#9EFF00] border-3 border-black py-4 font-black text-xs md:text-sm uppercase tracking-wider shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-all cursor-pointer flex items-center justify-center gap-2">
                      AdsManager Detaylarını İnceleyin →
                    </button>
                  </Link>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* MOBLIGO İŞ BİRLİĞİ SECTION */}
      <section className="cv-auto py-16 md:py-24 px-4 md:px-8 border-b-3 border-black bg-white relative overflow-hidden">
        {/* Decorative Grid Pattern */}
        <div className="absolute inset-0 opacity-5 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, black 1px, transparent 0)', backgroundSize: '20px 20px' }}></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="border-4 border-black p-8 md:p-12 bg-[#F9FAFB] shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-1 hover:shadow-[16px_16px_0px_0px_rgba(0,0,0,1)] transition-all duration-300">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center">
              
              {/* Left Side: Text and CTA */}
              <div className="lg:col-span-7 space-y-6">
                <div className="inline-block bg-[#9EFF00] text-black font-mono text-xs px-3 py-1 border-2 border-black uppercase font-bold shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
                  STRATEJİK MOBİL UYGULAMA ORTAĞIMIZ
                </div>
                <h2 className="font-black text-3xl md:text-5xl leading-none uppercase">
                  MAĞAZANIZI MOBİL <br />
                  <span className="bg-black text-white px-2 border-2 border-black inline-block my-1 shadow-[4px_4px_0px_0px_rgba(158,255,0,1)]">APP'E DÖNÜŞTÜRÜN</span>
                </h2>
                <p className="text-sm md:text-base text-neutral-800 font-bold leading-relaxed max-w-2xl">
                  E-ticaret satışlarınızı 3 kat daha fazla dönüşüm oranıyla uçuşa geçirin! Dijipo iş birliği ve <strong>Mobligo</strong> altyapısı sayesinde, Shopify veya ikas mağazanızı saniyeler içinde kod yazmadan profesyonel bir iOS & Android mobil uygulamaya dönüştürüyoruz.
                </p>
                
                <div className="flex flex-wrap gap-4 pt-2">
                  <div className="bg-white border-2 border-black px-4 py-2 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] font-mono text-xs font-black flex items-center gap-1.5">
                    <span className="text-green-600">●</span> 3x DÖNÜŞÜM ORANI (CR)
                  </div>
                  <div className="bg-white border-2 border-black px-4 py-2 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] font-mono text-xs font-black flex items-center gap-1.5">
                    <span className="text-green-600">●</span> KODSUZ TEMA EDİTÖRÜ
                  </div>
                  <div className="bg-white border-2 border-black px-4 py-2 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] font-mono text-xs font-black flex items-center gap-1.5">
                    <span className="text-green-600">●</span> %100 ANLIK BİLDİRİM (PUSH)
                  </div>
                </div>

                <div className="pt-4">
                  <a 
                    href="https://mobligo.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-block"
                  >
                    <button className="bg-black text-[#9EFF00] border-3 border-black px-8 py-4 font-black text-xs md:text-sm uppercase tracking-wider shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-all cursor-pointer flex items-center gap-2">
                      Mobligo'yu Keşfedin (Kodsuz Mobil App) →
                    </button>
                  </a>
                </div>
              </div>

              {/* Right Side: Mockup Frame */}
              <div className="lg:col-span-5 flex justify-center">
                <div className="w-full max-w-[280px] bg-white border-4 border-black p-4 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] relative rotate-2 hover:rotate-0 transition-transform duration-300">
                  <div className="absolute -top-3 -left-3 bg-[#9EFF00] text-black font-mono font-bold text-[10px] px-2 py-0.5 border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
                    iOS & ANDROID
                  </div>
                  <div className="aspect-[9/19] border-3 border-black bg-neutral-900 rounded-2xl overflow-hidden relative flex flex-col justify-between p-4">
                    {/* Speaker Notch */}
                    <div className="w-20 h-4 bg-black border-b-2 border-l-2 border-r-2 border-black absolute top-0 left-1/2 -translate-x-1/2 rounded-b-xl z-20 flex justify-center items-center">
                      <div className="w-8 h-1 bg-neutral-800 rounded-full"></div>
                    </div>
                    
                    {/* Screen Content */}
                    <div className="flex-1 flex flex-col justify-between pt-6 pb-2 text-white font-sans">
                      <div className="text-center space-y-1 mt-4">
                        <div className="text-[#9EFF00] font-black text-xl tracking-widest">MOBLIGO</div>
                        <div className="text-[10px] text-neutral-400 font-bold uppercase tracking-wider">Kodsuz Mobil Mağaza</div>
                      </div>

                      {/* Mockup Product Grid */}
                      <div className="grid grid-cols-2 gap-2 my-4">
                        <div className="bg-neutral-800 border border-neutral-700 p-2 rounded-lg space-y-1">
                          <div className="aspect-square bg-neutral-700 rounded flex items-center justify-center text-xs">🛍️</div>
                          <div className="h-2 bg-neutral-600 rounded w-3/4"></div>
                          <div className="h-2 bg-[#9EFF00] rounded w-1/2"></div>
                        </div>
                        <div className="bg-neutral-800 border border-neutral-700 p-2 rounded-lg space-y-1">
                          <div className="aspect-square bg-neutral-700 rounded flex items-center justify-center text-xs">👟</div>
                          <div className="h-2 bg-neutral-600 rounded w-3/4"></div>
                          <div className="h-2 bg-[#9EFF00] rounded w-1/2"></div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="bg-[#9EFF00] text-black text-[10px] font-black py-2 rounded-lg text-center uppercase tracking-wider">
                          HEMEN İNDİRİN
                        </div>
                        <div className="text-[8px] text-center text-neutral-400 font-bold">
                          E-Ticaretin Geleceği Mobilde!
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* MOBİL CİRO ARTIŞ HESAPLAYICI */}
      <section className="cv-auto py-16 md:py-24 px-4 md:px-8 border-b-3 border-black bg-[#F9FAFB] relative overflow-hidden">
        {/* Decorative Grid Pattern */}
        <div className="absolute inset-0 opacity-5 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, black 1px, transparent 0)', backgroundSize: '20px 20px' }}></div>
        
        <div className="max-w-5xl mx-auto relative z-10 space-y-12">
          <div className="text-center space-y-4 max-w-2xl mx-auto">
            <div className="inline-block bg-black text-[#9EFF00] font-mono text-xs px-3 py-1 border-2 border-black uppercase font-bold shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
              KAZANÇ POTANSİYELİ HESAPLAYICI
            </div>
            <h2 className="font-black text-3xl md:text-5xl leading-none uppercase">
              MOBİL APP KAZANCINIZI <br />
              <span className="underline decoration-[#9EFF00] decoration-wavy decoration-4">ANINDA HESAPLAYIN</span>
            </h2>
            <p className="text-xs md:text-sm text-neutral-600 font-bold">
              Mevcut e-ticaret cironuzu ve dönüşüm oranınızı girin; Mobligo altyapısıyla mobil uygulamaya geçtikten sonra elde edeceğiniz tahmini aylık ve yıllık ek kazancı anında görün!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-stretch">
            
            {/* Left Side: Inputs / Sliders */}
            <div className="md:col-span-6 bg-white border-4 border-black p-6 md:p-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] flex flex-col justify-between space-y-8">
              
              {/* Slider 1: Mevcut Aylık Ciro */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <label className="font-black text-xs md:text-sm uppercase tracking-wider text-black">Mevcut Aylık Ciro</label>
                  <span className="font-mono font-black text-sm md:text-base bg-[#9EFF00] text-black border-2 border-black px-2.5 py-0.5 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
                    {new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(currentMonthlyCiro)}
                  </span>
                </div>
                <input
                  aria-label="Mevcut aylık ciro"
                  type="range" 
                  min="50000" 
                  max="5000000" 
                  step="50000"
                  value={currentMonthlyCiro}
                  onChange={(e) => {
                    setCurrentMonthlyCiro(Number(e.target.value));
                    triggerConfetti();
                  }}
                  className="w-full h-3 bg-neutral-100 border-2 border-black rounded-none appearance-none cursor-pointer accent-black"
                />
                <div className="flex justify-between text-[10px] text-neutral-600 font-mono font-bold">
                  <span>50K TL</span>
                  <span>2.5M TL</span>
                  <span>5M TL</span>
                </div>
              </div>

              {/* Slider 2: Mevcut Dönüşüm Oranı */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <label className="font-black text-xs md:text-sm uppercase tracking-wider text-black">Mevcut Dönüşüm Oranı (CR)</label>
                  <span className="font-mono font-black text-sm md:text-base bg-black text-[#9EFF00] border-2 border-black px-2.5 py-0.5 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
                    %{currentCr.toFixed(2)}
                  </span>
                </div>
                <input
                  aria-label="Mevcut dönüşüm oranı"
                  type="range" 
                  min="0.1" 
                  max="5.0" 
                  step="0.1"
                  value={currentCr}
                  onChange={(e) => {
                    setCurrentCr(Number(e.target.value));
                    triggerConfetti();
                  }}
                  className="w-full h-3 bg-neutral-100 border-2 border-black rounded-none appearance-none cursor-pointer accent-black"
                />
                <div className="flex justify-between text-[10px] text-neutral-600 font-mono font-bold">
                  <span>%0.1</span>
                  <span>%2.5</span>
                  <span>%5.0</span>
                </div>
              </div>

              <div className="bg-neutral-50 border-2 border-black p-4 font-bold text-[11px] text-neutral-600 leading-relaxed">
                ℹ️ <strong>Neden 2.5 Kat Artış?</strong> Sektör ortalamalarına göre yerel mobil uygulamalar (Native App), push bildirimleri ve hızlı ödeme adımları sayesinde mobil web sitelerine göre ortalama %250 daha yüksek dönüşüm oranı (CR) sunar.
              </div>

            </div>

            {/* Right Side: Results */}
            <div className="md:col-span-6 bg-black text-white border-4 border-black p-6 md:p-8 shadow-[8px_8px_0px_0px_rgba(158,255,0,1)] flex flex-col justify-between space-y-6">
              
              <div className="space-y-6">
                <div className="border-b-2 border-neutral-800 pb-4">
                  <span className="font-mono text-[10px] font-bold text-neutral-400 uppercase">MOBİL APP SONRASI TAHMİNİ DEĞERLER</span>
                  <div className="flex justify-between items-baseline mt-1">
                    <span className="font-black text-xs md:text-sm uppercase text-neutral-300">Yeni Dönüşüm Oranı:</span>
                    <span className="font-mono font-black text-lg md:text-2xl text-[#9EFF00]">%{mobileCr.toFixed(2)}</span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <span className="font-mono text-[9px] font-bold text-neutral-400 uppercase">YENİ TAHMİNİ AYLIK CİRO</span>
                    <div className="font-mono font-black text-2xl md:text-4xl text-white mt-1">
                      {new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(estimatedMobileCiro)}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-2 border-t-2 border-neutral-800">
                    <div>
                      <span className="font-mono text-[9px] font-bold text-[#9EFF00] uppercase">AYLIK EK KAZANÇ</span>
                      <div className="font-mono font-black text-base md:text-xl text-[#9EFF00] mt-0.5">
                        +{new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(monthlyNetGain)}
                      </div>
                    </div>
                    <div>
                      <span className="font-mono text-[9px] font-bold text-[#9EFF00] uppercase">YILLIK EK KAZANÇ</span>
                      <div className="font-mono font-black text-base md:text-xl text-[#9EFF00] mt-0.5">
                        +{new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(yearlyNetGain)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => {
                  const formattedCiro = new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(currentMonthlyCiro);
                  const formattedGain = new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(monthlyNetGain);
                  setClientMessage(
                    `Merhaba Dijipo! Mobligo mobil ciro artış hesaplama aracınızda şu verileri hesapladım:\n• Mevcut Aylık Ciro: ${formattedCiro}\n• Mevcut Dönüşüm Oranı: %${currentCr.toFixed(2)}\n• Tahmini Aylık Ek Kazanç: ${formattedGain}\n\nMağazamızı mobil uygulamaya dönüştürmek ve bu büyüme potansiyelini yakalamak için detaylı bilgi almak istiyoruz.`
                  );
                  setIsOfferOpen(true);
                }}
                className="w-full bg-[#9EFF00] text-black border-2 border-black py-4 font-black text-xs md:text-sm uppercase tracking-wider shadow-[4px_4px_0px_0px_rgba(255,255,255,1)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px_rgba(255,255,255,1)] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-all cursor-pointer flex items-center justify-center gap-2"
              >
                🚀 Bu Büyümeyi Yakalayın →
              </button>

            </div>

          </div>
        </div>
      </section>

      {/* MOBİL PUSH BİLDİRİM SİMÜLASYONU */}
      <section className="cv-auto py-16 md:py-24 px-4 md:px-8 border-b-3 border-black bg-white relative overflow-hidden">
        {/* Decorative Grid Pattern */}
        <div className="absolute inset-0 opacity-5 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, black 1px, transparent 0)', backgroundSize: '20px 20px' }}></div>
        
        <div className="max-w-5xl mx-auto relative z-10 space-y-12">
          <div className="text-center space-y-4 max-w-2xl mx-auto">
            <div className="inline-block bg-[#9EFF00] text-black font-mono text-xs px-3 py-1 border-2 border-black uppercase font-bold shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
              ETKİLEŞİMLİ SİMÜLASYON
            </div>
            <h2 className="font-black text-3xl md:text-5xl leading-none uppercase">
              PUSH BİLDİRİM <br />
              <span className="bg-black text-white px-2 border-2 border-black inline-block my-1 shadow-[4px_4px_0px_0px_rgba(158,255,0,1)]">GÜCÜNÜ TEST EDİN</span>
            </h2>
            <p className="text-xs md:text-sm text-neutral-600 font-bold">
              Mobil uygulamaların e-ticaret satışlarını neden uçurduğunu canlı test edin! Aşağıdaki e-ticaret senaryolarından birini seçin ve akıllı telefon simülatörüne anlık bildirim gönderin.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            
            {/* Left Side: Interactive Buttons */}
            <div className="md:col-span-6 space-y-6">
              <h3 className="font-black text-xl uppercase text-black border-b-2 border-black pb-2">Senaryo Seçin</h3>
              <p className="text-xs text-neutral-500 font-bold">Ziyaretçilerinizi sadık müşterilere dönüştüren en popüler 3 anlık bildirim kurgusunu test edin:</p>
              
              <div className="space-y-4">
                {notificationScenarios.map((scenario) => (
                  <button
                    key={scenario.id}
                    onClick={() => triggerPushNotification(scenario)}
                    disabled={isNotificationSending}
                    className="w-full text-left bg-white border-3 border-black p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-all cursor-pointer flex items-center justify-between group"
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl bg-neutral-100 border-2 border-black p-2 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] group-hover:bg-[#9EFF00] transition-colors">{scenario.icon}</span>
                      <div>
                        <h4 className="font-black text-xs uppercase text-black">{scenario.label}</h4>
                        <p className="text-[10px] text-neutral-600 font-bold mt-0.5 line-clamp-1">{scenario.title}</p>
                      </div>
                    </div>
                    <span className="font-mono text-[10px] font-black bg-black text-[#9EFF00] px-2 py-0.5 border border-black uppercase group-hover:bg-[#9EFF00] group-hover:text-black transition-colors">Test Et →</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Right Side: Smartphone Simulator */}
            <div className="md:col-span-6 flex justify-center">
              <div className="w-full max-w-[280px] bg-white border-4 border-black p-4 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] relative">
                <div className="absolute -top-3 -right-3 bg-black text-[#9EFF00] font-mono font-bold text-[10px] px-2 py-0.5 border-2 border-black shadow-[2px_2px_0px_0px_rgba(158,255,0,1)]">
                  SİMÜLATÖR
                </div>
                
                <div className="aspect-[9/19] border-3 border-black bg-neutral-950 rounded-2xl overflow-hidden relative flex flex-col justify-between p-4">
                  {/* Speaker Notch */}
                  <div className="w-20 h-4 bg-black border-b-2 border-l-2 border-r-2 border-black absolute top-0 left-1/2 -translate-x-1/2 rounded-b-xl z-20 flex justify-center items-center">
                    <div className="w-8 h-1 bg-neutral-800 rounded-full"></div>
                  </div>
                  
                  {/* Phone Header / Status Bar */}
                  <div className="flex justify-between items-center text-[8px] text-neutral-400 font-mono font-bold pt-2">
                    <span>09:41</span>
                    <div className="flex items-center space-x-1">
                      <span>📶</span>
                      <span>🔋 100%</span>
                    </div>
                  </div>

                  {/* Phone Screen Content / Push Notification Popup Area */}
                  <div className="flex-1 flex flex-col justify-start items-center pt-8 relative">
                    
                    {/* Simulated Push Notification Card */}
                    {isNotificationSending && (
                      <div className="w-full bg-neutral-900 border-2 border-[#9EFF00] p-3 shadow-[0px_4px_12px_rgba(158,255,0,0.2)] rounded-xl animate-pulse text-center py-6 space-y-2">
                        <div className="text-[#9EFF00] text-lg">📡</div>
                        <div className="text-[10px] text-neutral-300 font-bold uppercase tracking-wider">Sinyal Gönderiliyor...</div>
                      </div>
                    )}

                    {activeNotification && !isNotificationSending && (
                      <div className="w-full bg-white border-3 border-black p-3.5 shadow-[4px_4px_0px_0px_rgba(158,255,0,1)] rounded-xl animate-zoom-in text-black space-y-2 relative z-30">
                        <div className="flex justify-between items-start">
                          <div className="flex items-center space-x-1.5">
                            <span className="text-xs">{activeNotification.icon}</span>
                            <span className="font-black text-[9px] uppercase tracking-wider text-black">MOBLIGO</span>
                          </div>
                          <span className="text-[8px] text-neutral-400 font-mono font-bold">{activeNotification.time}</span>
                        </div>
                        <div className="space-y-1">
                          <h4 className="font-black text-[10px] leading-tight uppercase text-black">{activeNotification.title}</h4>
                          <p className="text-[9px] text-neutral-600 font-bold leading-relaxed">{activeNotification.body}</p>
                        </div>
                        {/* Decorative Slide to open indicator */}
                        <div className="border-t border-neutral-100 pt-2 mt-1 flex justify-center">
                          <span className="text-[7px] text-neutral-400 font-black uppercase tracking-widest animate-pulse">Açmak için dokunun ➔</span>
                        </div>
                      </div>
                    )}

                    {!activeNotification && !isNotificationSending && (
                      <div className="text-center py-16 space-y-3">
                        <div className="text-4xl animate-bounce">📱</div>
                        <p className="text-[9px] text-neutral-400 font-bold max-w-[180px] mx-auto leading-relaxed">
                          Soldaki senaryolardan birine tıklayarak anlık bildirim gönderin ve simülasyonu başlatın!
                        </p>
                      </div>
                    )}

                  </div>

                  {/* Home Indicator Bar */}
                  <div className="w-24 h-1 bg-neutral-700 rounded-full mx-auto mb-1 shrink-0"></div>

                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* QUICK PHONE CALL / SİZİ ARAYALIM SECTION */}
      <section className="cv-auto py-16 md:py-20 px-4 md:px-8 border-b-3 border-black bg-black text-white">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          
          <div className="space-y-3 max-w-xl">
            <div className="inline-block bg-[#9EFF00] text-black font-mono text-[10px] px-2 py-0.5 border border-black uppercase font-bold">
              HIZLI İLETİŞİM HATTI
            </div>
            <h3 className="font-black text-2xl md:text-4xl uppercase">SİZİ ARAYALIM!</h3>
            <p className="text-xs md:text-sm text-neutral-400 font-medium">
              E-ticaret hedeflerinizi telefonda konuşalım. Numaranızı bırakın, uzman büyüme danışmanlarımız 15 dakika içinde sizi arasın.
            </p>
          </div>

          <div className="w-full md:w-auto shrink-0">
            {isCallSubmitted ? (
              <div className="bg-[#9EFF00] text-black border-2 border-black p-4 font-black text-xs md:text-sm uppercase tracking-wider shadow-[3px_3px_0px_0px_rgba(255,255,255,1)] animate-zoom-in text-center">
                ⏱️ Talebiniz alındı! 15 dakika içinde aranacaksınız.
              </div>
            ) : (
              <form onSubmit={handleCallSubmit} className="flex flex-col sm:flex-row gap-3">
                <input 
                  type="text" 
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Adınız Soyadınız"
                  required
                  className="bg-white border-2 border-black p-3 text-black font-bold text-xs focus:outline-none placeholder:text-neutral-400 w-full sm:w-48"
                />
                <input 
                  type="tel" 
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                  placeholder="Telefon Numaranız"
                  required
                  className="bg-white border-2 border-black p-3 text-black font-bold text-xs focus:outline-none placeholder:text-neutral-400 w-full sm:w-48"
                />
                <button 
                  type="submit"
                  className="bg-[#9EFF00] text-black border-2 border-black px-6 py-3 font-black text-xs uppercase tracking-wider shadow-[3px_3px_0px_0px_rgba(255,255,255,1)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0px_0px_rgba(255,255,255,1)] transition-all cursor-pointer"
                >
                  Beni Arayın
                </button>
              </form>
            )}
          </div>

        </div>
      </section>

      {/* SEO: E-TİCARET REKLAM AJANSI TANITIM + SEKTÖREL BAĞLANTILAR */}
      <section className="cv-auto py-16 md:py-20 px-4 md:px-8 bg-white border-b-3 border-black">
        <div className="max-w-5xl mx-auto space-y-8">
          <div className="space-y-4">
            <h2 className="font-black text-2xl md:text-4xl tracking-tight">
              E-Ticaret Markaları İçin Performans Odaklı Reklam Yönetimi
            </h2>
            <p className="text-sm md:text-base text-neutral-600 font-semibold leading-relaxed">
              E-ticaret reklam ajansı olarak markaların Google Ads, Instagram ve Facebook reklam
              süreçlerini yönetiyor, reklam bütçelerinin daha verimli kullanılmasını hedefliyoruz.
              Kadın giyim, tesettür giyim, butik, kozmetik, takı, aksesuar, ayakkabı, çanta, ev
              dekorasyon ve farklı e-ticaret sektörlerine özel dijital pazarlama stratejileri
              geliştiriyoruz. Meta Pixel + Conversions API kurulumundan Google Alışveriş feed
              optimizasyonuna, dinamik yeniden pazarlamadan dönüşüm odaklı kreatif üretimine kadar
              tüm performans pazarlaması sürecini tek çatı altında yürütüyoruz.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {[
              { h: "/sektorel/kadin-giyim-reklam-ajansi", t: "Kadın Giyim Reklam Ajansı" },
              { h: "/sektorel/tesettur-giyim-dijital-pazarlama", t: "Tesettür Giyim Reklam Ajansı" },
              { h: "/sektorel/butikler-icin-instagram-reklamlari", t: "Butik Reklam Ajansı" },
              { h: "/sektorel/kozmetik-eticaret-reklam-ajansi", t: "Kozmetik Reklam Ajansı" },
              { h: "/sektorel/taki-aksesuar-reklam-ajansi", t: "Takı & Aksesuar Reklam Ajansı" },
              { h: "/sektorel/ayakkabi-canta-eticaret-reklamlari", t: "Ayakkabı & Çanta Reklamları" },
              { h: "/sektorel/ev-dekorasyon-eticaret-reklamlari", t: "Ev Dekorasyon Reklamları" },
              { h: "/sektorel/e-ticaret-google-ads-ajansi", t: "E-Ticaret Google Ads" },
              { h: "/hizmetlerimiz/reklam-yonetimi", t: "Reklam Yönetimi Hizmeti" },
              { h: "/shopify/magaza-kurulumu", t: "Shopify Mağaza Kurulumu" },
            ].map(link => (
              <Link
                key={link.h}
                href={link.h}
                className="bg-white text-black border-2 border-black px-4 py-2 font-bold text-xs shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:bg-[#9EFF00] hover:translate-x-[1px] hover:translate-y-[1px] transition-all cursor-pointer"
              >
                {link.t}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* DYNAMIC BLOG / REHBER MODÜLÜ (ADDED FOR ORGANIC TRAFFIC & SEO/GEO) */}
      <section className="cv-auto py-20 md:py-28 px-4 md:px-8 border-b-3 border-black bg-white">
        <div className="max-w-7xl mx-auto">
          
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div className="space-y-4 max-w-2xl">
              <div className="inline-block bg-[#9EFF00] text-black font-mono text-xs px-3 py-1 border-2 border-black uppercase font-bold shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
                DİJİPO AKADEMİ & REHBER
              </div>
              <h2 className="font-black text-3xl md:text-5xl leading-none text-black uppercase">
                ORGANİK BÜYÜME VE <br />
                <span className="bg-black text-white px-2 border-2 border-black inline-block my-1 shadow-[4px_4px_0px_0px_rgba(158,255,0,1)]">E-TİCARET İPUÇLARI</span>
              </h2>
              <p className="text-sm md:text-base text-neutral-600 font-bold">
                E-ticaret sitenizin organik trafiğini, Google ve yapay zeka arama motorlarındaki (SEO & GEO) görünürlüğünü artıracak rehber içeriklerimiz.
              </p>
            </div>
            
            <div className="shrink-0">
              <Link href="/blog">
                <button className="bg-white text-black border-3 border-black px-6 py-3 font-black text-xs uppercase tracking-wider shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-all cursor-pointer flex items-center gap-1">
                  Tüm Yazıları Gör <ChevronRight className="w-4 h-4" />
                </button>
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {latestPosts.map((post) => (
              <div 
                key={post.id}
                className="bg-white border-3 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-1.5 hover:-translate-x-1 hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transition-all duration-200 flex flex-col justify-between"
              >
                <div>
                  <div className="aspect-video border-b-3 border-black overflow-hidden relative bg-neutral-100">
                    {post.image.startsWith("/") ? (
                      // Yerel marka kapağı (1200x630): Unsplash boyut parametresi değiştirilmez, gri tonlama yok
                      <img loading="lazy" decoding="async" width={1200} height={630} src={post.image} alt={post.title} className="w-full h-full object-cover bg-white" />
                    ) : (
                      <img loading="lazy" decoding="async" width={800} height={450} src={post.image.replace("w=800", "w=480")} alt={post.title} className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-300" />
                    )}
                    <span className="absolute top-3 left-3 bg-[#9EFF00] text-black font-mono font-bold text-[9px] px-2 py-0.5 border-2 border-black shadow-[1.5px_1.5px_0px_0px_rgba(0,0,0,1)] uppercase">
                      {post.category}
                    </span>
                  </div>
                  
                  <div className="p-6 space-y-3">
                    <div className="flex items-center space-x-4 font-mono text-[10px] text-neutral-600 font-bold">
                      <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {post.date}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {post.readTime}</span>
                    </div>
                    <h3 className="font-black text-lg text-black uppercase leading-tight hover:text-neutral-700 transition-colors">
                      <Link href={`/blog/${post.id}`}>{post.title}</Link>
                    </h3>
                    <p className="text-xs text-neutral-500 font-semibold leading-relaxed line-clamp-3">
                      {post.summary}
                    </p>
                  </div>
                </div>

                <div className="p-6 pt-0">
                  <Link href={`/blog/${post.id}`} className="inline-flex items-center font-black text-xs uppercase tracking-wider text-black hover:text-neutral-600 transition-colors gap-1">
                    Okumaya Devam Et <ArrowRight className="w-4 h-4 text-[#9EFF00]" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* SERTİFİKA VE ROZET (CERTIFICATES & BADGES) SECTION */}
      <section className="cv-auto py-20 md:py-28 px-4 md:px-8 border-b-3 border-black bg-gray-50">
        <div className="max-w-7xl mx-auto">

          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <div className="inline-block bg-[#9EFF00] text-black font-mono text-xs px-3 py-1 border-2 border-black uppercase font-bold shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
              SERTİFİKA & ROZETLER
            </div>
            <h2 className="font-black text-3xl md:text-5xl leading-none text-black uppercase">
              RESMİ İŞ ORTAKLIKLARI VE{" "}
              <span className="bg-black text-white px-2 border-2 border-black inline-block my-1 shadow-[4px_4px_0px_0px_rgba(158,255,0,1)]">
                SERTİFİKALARIMIZ
              </span>
            </h2>
            <p className="text-sm md:text-base text-neutral-600 font-bold leading-relaxed">
              Dünyanın en büyük teknoloji platformlarının resmi iş ortağı ve sertifikalı uzmanlarıyız. Markanız en doğru ellerde.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">

            {/* Badge 1 - Meta Business Partner */}
            <div className="bg-white border-3 border-black p-5 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-1 hover:-translate-x-0.5 hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-all duration-300 ease-out flex flex-col items-center text-center space-y-3 group">
              <div className="w-12 h-12 bg-[#9EFF00] border-2 border-black flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] group-hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all duration-300">
                <ShieldCheck className="h-6 w-6 text-black" />
              </div>
              <h3 className="font-black text-xs uppercase text-black leading-tight">Meta Business Partner</h3>
              <p className="text-[10px] text-neutral-500 font-semibold leading-relaxed">Facebook & Instagram reklamlarında resmi iş ortağı</p>
            </div>

            {/* Badge 2 - Google Partner */}
            <div className="bg-white border-3 border-black p-5 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-1 hover:-translate-x-0.5 hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-all duration-300 ease-out flex flex-col items-center text-center space-y-3 group">
              <div className="w-12 h-12 bg-black border-2 border-black flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(158,255,0,1)] group-hover:shadow-[3px_3px_0px_0px_rgba(158,255,0,1)] transition-all duration-300">
                <Globe className="h-6 w-6 text-[#9EFF00]" />
              </div>
              <h3 className="font-black text-xs uppercase text-black leading-tight">Google Partner</h3>
              <p className="text-[10px] text-neutral-500 font-semibold leading-relaxed">Google Ads sertifikalı reklam yönetimi ortağı</p>
            </div>

            {/* Badge 3 - ikas Resmi İş Ortağı */}
            <div className="bg-white border-3 border-black p-5 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-1 hover:-translate-x-0.5 hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-all duration-300 ease-out flex flex-col items-center text-center space-y-3 group">
              <div className="w-12 h-12 bg-[#9EFF00] border-2 border-black flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] group-hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all duration-300">
                <BadgeCheck className="h-6 w-6 text-black" />
              </div>
              <h3 className="font-black text-xs uppercase text-black leading-tight">ikas Resmi İş Ortağı</h3>
              <p className="text-[10px] text-neutral-500 font-semibold leading-relaxed">ikas e-ticaret altyapısı onaylı çözüm ortağı</p>
            </div>

            {/* Badge 4 - Shopify Partner */}
            <div className="bg-white border-3 border-black p-5 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-1 hover:-translate-x-0.5 hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-all duration-300 ease-out flex flex-col items-center text-center space-y-3 group">
              <div className="w-12 h-12 bg-black border-2 border-black flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(158,255,0,1)] group-hover:shadow-[3px_3px_0px_0px_rgba(158,255,0,1)] transition-all duration-300">
                <ShoppingBag className="h-6 w-6 text-[#9EFF00]" />
              </div>
              <h3 className="font-black text-xs uppercase text-black leading-tight">Shopify Partner</h3>
              <p className="text-[10px] text-neutral-500 font-semibold leading-relaxed">Shopify resmi geliştirici ve büyüme ortağı</p>
            </div>

            {/* Badge 5 - TikTok Marketing Partner */}
            <div className="bg-white border-3 border-black p-5 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-1 hover:-translate-x-0.5 hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-all duration-300 ease-out flex flex-col items-center text-center space-y-3 group">
              <div className="w-12 h-12 bg-[#9EFF00] border-2 border-black flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] group-hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all duration-300">
                <Smartphone className="h-6 w-6 text-black" />
              </div>
              <h3 className="font-black text-xs uppercase text-black leading-tight">TikTok Marketing Partner</h3>
              <p className="text-[10px] text-neutral-500 font-semibold leading-relaxed">TikTok for Business sertifikalı pazarlama ortağı</p>
            </div>

            {/* Badge 6 - Google Analytics Certified */}
            <div className="bg-white border-3 border-black p-5 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-1 hover:-translate-x-0.5 hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-all duration-300 ease-out flex flex-col items-center text-center space-y-3 group">
              <div className="w-12 h-12 bg-black border-2 border-black flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(158,255,0,1)] group-hover:shadow-[3px_3px_0px_0px_rgba(158,255,0,1)] transition-all duration-300">
                <BarChart3 className="h-6 w-6 text-[#9EFF00]" />
              </div>
              <h3 className="font-black text-xs uppercase text-black leading-tight">Google Analytics Certified</h3>
              <p className="text-[10px] text-neutral-500 font-semibold leading-relaxed">GA4 sertifikalı veri analizi ve raporlama uzmanı</p>
            </div>

          </div>

        </div>
      </section>

      {/* FOOTER CTA SECTION */}
      <section className="cv-auto py-20 md:py-28 px-4 md:px-8 border-b-3 border-black bg-[#9EFF00] text-black relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,0,0,0.04)_0,transparent_100%)] pointer-events-none"></div>
        <div className="max-w-4xl mx-auto text-center space-y-8 relative z-10">
          <h2 className="font-black text-3xl md:text-6xl tracking-tight leading-none uppercase">
            SİTENİZİ VE REKLAMLARINIZI <br />
            <span className="bg-black text-white px-3 py-1 inline-block my-1 border-2 border-black shadow-[4px_4px_0px_0px_rgba(255,255,255,1)]">ÜCRETSİZ ANALİZ EDELİM</span>
          </h2>
          <p className="text-sm md:text-lg font-bold leading-relaxed max-w-2xl mx-auto">
            Hangi altyapıyı kullanırsanız kullanın, mevcut durumunuzu analiz edip cirolarınızı nasıl katlayabileceğinizi anlatan detaylı bir rapor hazırlayalım.
          </p>
          
          <div className="pt-4 flex flex-col sm:flex-row justify-center gap-4">
            <button 
              data-cta
              onClick={() => setIsOfferOpen(true)}
              className="bg-black text-[#9EFF00] border-3 border-black px-8 py-4 font-black text-sm uppercase tracking-wider shadow-[4px_4px_0px_0px_rgba(255,255,255,1)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px_rgba(255,255,255,1)] transition-all cursor-pointer"
            >
              Ücretsiz Analiz Alın
            </button>
            <button 
              data-cta
              onClick={() => setIsCallOpen(true)}
              className="bg-white text-black border-3 border-black px-8 py-4 font-black text-sm uppercase tracking-wider shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all cursor-pointer"
            >
              Sizi Arayalım
            </button>
          </div>
        </div>
      </section>

      {/* ÜCRETSİZ ANALİZ AL / TEKLİF MODAL */}
      {isContactOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs">
          <div className="relative bg-white border-4 border-black w-full max-w-lg shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-6 md:p-8 text-black animate-zoom-in">
            
            <button 
              onClick={() => { setIsOfferOpen(false); setIsFormSubmitted(false); }}
              className="absolute top-4 right-4 bg-white border-2 border-black p-1 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none transition-all cursor-pointer"
            >
              <X className="h-4 w-4" />
            </button>

            {!isFormSubmitted ? (
              <div className="space-y-4">
                <div className="space-y-1">
                  <span className="inline-block bg-[#9EFF00] text-black font-mono text-[10px] px-2 py-0.5 border border-black uppercase font-bold">
                    ÜCRETSİZ BÜYÜME ANALİZİ
                  </span>
                  <h3 className="font-black text-2xl uppercase">Analiz Talebi</h3>
                  <p className="text-xs text-neutral-500 font-semibold">
                    E-ticaret sitenizi ve reklam hesaplarınızı inceleyerek cironuzu artıracak fırsatları raporlayalım.
                  </p>
                </div>

                <form onSubmit={handleOfferSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold block">ADINIZ SOYADINIZ</label>
                      <input 
                        type="text" 
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                        placeholder="Örn: Ahmet Yılmaz"
                        className="w-full border-2 border-black p-2 text-sm focus:outline-none font-bold"
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold block">E-POSTA ADRESİNİZ</label>
                      <input 
                        type="email" 
                        value={clientEmail}
                        onChange={(e) => setClientEmail(e.target.value)}
                        placeholder="Örn: ahmet@markaniz.com"
                        className="w-full border-2 border-black p-2 text-sm focus:outline-none font-bold"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold block">TELEFON NUMARANIZ</label>
                      <input 
                        type="tel" 
                        value={clientPhone}
                        onChange={(e) => setClientPhone(e.target.value)}
                        placeholder="Örn: 0532 XXX XX XX"
                        className="w-full border-2 border-black p-2 text-sm focus:outline-none font-bold"
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold block">E-TİCARET ALTYAPINIZ</label>
                      <select 
                        value={clientPlatform}
                        onChange={(e) => setClientPlatform(e.target.value)}
                        className="w-full border-2 border-black p-2 text-sm focus:outline-none font-bold bg-white"
                      >
                        <option value="ikas">ikas</option>
                        <option value="shopify">Shopify</option>
                        <option value="other">Diğer / Bilmiyorum</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold block">MESAJINIZ / HEDEFLERİNİZ</label>
                    <textarea 
                      value={clientMessage}
                      onChange={(e) => setClientMessage(e.target.value)}
                      placeholder="Siteniz, bütçeniz ve hedefleriniz hakkında kısa bilgi..."
                      rows={4}
                      className="w-full border-2 border-black p-2 text-sm focus:outline-none font-bold resize-none"
                    ></textarea>
                  </div>

                  <button 
                    type="submit"
                    className="w-full bg-[#9EFF00] text-black border-2 border-black py-3 font-black text-xs uppercase tracking-wider shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none transition-all cursor-pointer"
                  >
                    Ücretsiz Analiz Talebini Gönder
                  </button>
                </form>
              </div>
            ) : (
              <div className="text-center py-8 space-y-4 animate-zoom-in">
                <div className="w-16 h-16 bg-[#9EFF00] border-4 border-black flex items-center justify-center mx-auto shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
                  <CheckCircle2 className="w-10 h-10 text-black" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-xl font-bold uppercase">Talebiniz Güvende!</h3>
                  <p className="text-xs text-neutral-600 font-semibold max-w-xs mx-auto">
                    Analiz talebiniz e-ticaret büyüme mühendislerimize ulaştı. Sitenizi detaylıca inceleyip en geç 24 saat içinde sizinle iletişime geçeceğiz.
                  </p>
                </div>
                <button 
                  onClick={() => { setIsOfferOpen(false); setIsFormSubmitted(false); }}
                  className="bg-black text-white border-2 border-black px-6 py-2 font-bold text-xs shadow-[3px_3px_0px_0px_rgba(158,255,0,1)] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none transition-all"
                >
                  Kapat
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ÜCRETSİZ TAKVİM / MEET TOPLANTI PLANLAMA MODAL */}
      {isCalendarOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 md:p-4 backdrop-blur-xs overflow-y-auto">
          <div className="relative bg-white border-4 border-black w-full max-w-lg md:max-w-2xl shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-4 md:p-8 text-black animate-zoom-in max-h-[95vh] overflow-y-auto">
            
            <button 
              onClick={() => setIsCalendarOpen(false)}
              className="absolute top-2 right-2 md:top-4 md:right-4 bg-white border-2 border-black p-1.5 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none transition-all cursor-pointer z-10"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              
              {/* Left Column: Info & Google Meet Details */}
              <div className="md:col-span-5 space-y-4 border-b-2 md:border-b-0 md:border-r-2 border-black pb-4 md:pb-0 md:pr-6">
                <div className="space-y-1">
                  <span className="inline-block bg-[#9EFF00] text-black font-mono text-[9px] px-2 py-0.5 border border-black uppercase font-bold">
                    ÜCRETSİZ 15 DK STRATEJİ SEANSI
                  </span>
                  <h3 className="font-black text-xl uppercase">Meet Toplantısı</h3>
                  <p className="text-xs text-neutral-500 font-semibold">
                    E-ticaret sitenizi büyütmek, ikas/Shopify geçiş süreçlerini konuşmak ve reklam stratejisi oluşturmak için anında randevu alın.
                  </p>
                </div>

                <div className="space-y-2 text-[11px] font-bold text-neutral-600">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">⏱️</span>
                    <span>15 Dakika Birebir Görüşme</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">🎥</span>
                    <span>Google Meet (Online Video Görüşme)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">📈</span>
                    <span>Ücretsiz Reklam ve Altyapı Analizi</span>
                  </div>
                </div>

                <div className="bg-neutral-50 border-2 border-black p-3 text-[10px] text-neutral-500 font-bold leading-relaxed">
                  💡 <strong>Nasıl Çalışır?</strong> Takvimden size en uygun gün ve saati seçin. Toplantı onaylandığında Google Meet katılım linki anında e-posta adresinize gönderilecektir.
                </div>
              </div>

              {/* Right Column: Free Interactive Scheduler (Simulating Cal.com/Calendly) */}
              <div className="md:col-span-7 space-y-4">
                <h4 className="font-black text-xs uppercase tracking-wider text-black flex items-center gap-1">
                  <span>📅</span> Uygun Bir Zaman Seçin
                </h4>
                
                {/* Embed a beautiful, interactive Cal.com/Calendly Simulator Widget */}
                <div className="border-3 border-black bg-neutral-50 p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] rounded-none space-y-4">
                  
                  {/* Step 1: Date Picker Simulator */}
                  <div className="grid grid-cols-4 gap-2 text-center">
                    {[
                      { day: "Pzt", date: "25 Mayıs", available: true },
                      { day: "Sal", date: "26 Mayıs", available: true },
                      { day: "Çar", date: "27 Mayıs", available: true },
                      { day: "Per", date: "28 Mayıs", available: false },
                    ].map((d, i) => (
                      <div 
                        key={i} 
                        className={`border-2 border-black p-2 cursor-pointer transition-all ${
                          d.available 
                            ? "bg-white hover:bg-[#9EFF00] shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none" 
                            : "bg-neutral-200 text-neutral-400 cursor-not-allowed"
                        }`}
                      >
                        <div className="text-[9px] font-mono uppercase">{d.day}</div>
                        <div className="text-[10px] font-black">{d.date}</div>
                      </div>
                    ))}
                  </div>

                  {/* Step 2: Time Slots Simulator */}
                  <div className="space-y-2">
                    <div className="text-[10px] font-black uppercase text-neutral-400">UYGUN SAATLER (TR SAATİ)</div>
                    <div className="grid grid-cols-3 gap-2">
                      {["10:00", "11:30", "14:00", "15:30", "16:45"].map((time, i) => (
                        <button
                          key={i}
                          onClick={() => {
                            alert(`Harika seçim! ${time} toplantı saati rezerve edildi. Şimdi aşağıdaki formu doldurarak Google Meet linkinizi oluşturun.`);
                            setIsOfferOpen(true);
                            setIsCalendarOpen(false);
                          }}
                          className="bg-white border-2 border-black py-1.5 text-xs font-mono font-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:bg-black hover:text-[#9EFF00] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none active:translate-x-[2px] active:translate-y-[2px] transition-all cursor-pointer"
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="text-center pt-2 border-t border-black/10">
                    <span className="text-[9px] text-neutral-400 font-bold">🛡️ Cal.com & Google Meet altyapısıyla güvenli bir şekilde entegre edilmiştir.</span>
                  </div>

                </div>

                <div className="flex justify-between items-center pt-2">
                  <span className="text-[10px] text-neutral-500 font-bold">Doğrudan form doldurmak mı istersiniz?</span>
                  <button 
                    onClick={() => { setIsOfferOpen(true); setIsCalendarOpen(false); }}
                    className="text-[10px] font-black uppercase text-black underline hover:text-neutral-600"
                  >
                    Analiz Formu Aç →
                  </button>
                </div>

              </div>

            </div>

          </div>
        </div>
      )}

      {/* SİZİ ARAYALIM MODAL */}
      {isCallOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs">
          <div className="relative bg-white border-4 border-black w-full max-w-md shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-6 space-y-4 text-black">
            
            <button 
              onClick={() => { setIsCallOpen(false); setIsFormCallSubmitted(false); }}
              className="absolute top-4 right-4 bg-white border-2 border-black p-1 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none transition-all cursor-pointer"
            >
              <X className="h-4 w-4" />
            </button>

            {!isCallSubmitted ? (
              <>
                <h3 className="font-black text-xl">Sizi Arayalım</h3>
                <p className="text-xs text-neutral-600 font-semibold">
                  Numaranızı bırakın, e-ticaret uzmanlarımız 15 dakika içinde sizi arayarak büyüme hedeflerinizi dinlesin.
                </p>

                <form onSubmit={handleCallSubmit} className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold block">ADINIZ SOYADINIZ</label>
                    <input 
                      type="text" 
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder="Örn: Ahmet Yılmaz"
                      className="w-full border-2 border-black p-2 text-sm focus:outline-none"
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold block">TELEFON NUMARANIZ</label>
                    <input 
                      type="tel" 
                      value={clientPhone}
                      onChange={(e) => setClientPhone(e.target.value)}
                      placeholder="Örn: 0532 XXX XX XX"
                      className="w-full border-2 border-black p-2 text-sm focus:outline-none"
                      required
                    />
                  </div>

                  <button 
                    type="submit"
                    className="w-full bg-[#9EFF00] text-black border-2 border-black py-3 font-bold text-sm shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none transition-all cursor-pointer"
                  >
                    Beni Arayın
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-8 space-y-4 animate-zoom-in">
                <div className="w-16 h-16 bg-[#9EFF00] border-4 border-black flex items-center justify-center mx-auto shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
                  <CheckCircle2 className="w-10 h-10 text-black" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-xl font-bold">Talebiniz Alındı!</h3>
                  <p className="text-xs text-neutral-600 font-semibold max-w-xs mx-auto">
                    Müşteri temsilcimiz 15 dakika içinde telefon veya WhatsApp üzerinden sizinle iletişime geçecektir.
                  </p>
                </div>
                <button 
                  onClick={() => { setIsCallOpen(false); setIsFormCallSubmitted(false); }}
                  className="bg-black text-white border-2 border-black px-6 py-2 font-bold text-xs shadow-[3px_3px_0px_0px_rgba(158,255,0,1)] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none transition-all"
                >
                  Kapat
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* YORUM YAP MODAL */}
      {isFeedbackOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs">
          <div className="relative bg-white border-4 border-black w-full max-w-md shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-6 space-y-4 text-black">
            
            <button 
              onClick={() => { setIsFeedbackOpen(false); setIsFeedbackSubmitted(false); }}
              className="absolute top-4 right-4 bg-white border-2 border-black p-1 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none transition-all cursor-pointer"
            >
              <X className="h-4 w-4" />
            </button>

            {!isFeedbackSubmitted ? (
              <>
                <h3 className="font-black text-xl uppercase">Deneyiminizi Paylaşın</h3>
                <p className="text-xs text-neutral-600 font-semibold">
                  Dijipo ile elde ettiğiniz başarıyı ve reklam deneyiminizi paylaşın, sitemizde referans olarak yerinizi alın!
                </p>

                <form onSubmit={(e) => {
                  e.preventDefault();
                  formSubmitMutation.mutate(
                    {
                      formType: "feedback",
                      name: feedbackName,
                      metadata: JSON.stringify({
                        company: feedbackCompany,
                        platform: feedbackPlatform,
                        roas: feedbackRoas,
                        feedbackText: feedbackText,
                      }),
                    },
                    {
                      onSuccess: () => {
                        setIsFeedbackSubmitted(true);
                        triggerConfetti();
                      },
                      onError: () => {
                        setIsFeedbackSubmitted(true);
                        triggerConfetti();
                      },
                    }
                  );
                }} className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold block">ADINIZ SOYADINIZ *</label>
                    <input 
                      type="text" 
                      value={feedbackName}
                      onChange={(e) => setFeedbackName(e.target.value)}
                      placeholder="Örn: Ahmet Yılmaz"
                      className="w-full border-2 border-black p-2 text-sm focus:outline-none"
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold block">ŞİRKETİNİZ & UNVANINIZ *</label>
                    <input 
                      type="text" 
                      value={feedbackCompany}
                      onChange={(e) => setFeedbackCompany(e.target.value)}
                      placeholder="Örn: Kurucu, Süvari E-Ticaret"
                      className="w-full border-2 border-black p-2 text-sm focus:outline-none"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-bold block">ALTYAPINIZ</label>
                      <select 
                        value={feedbackPlatform}
                        onChange={(e) => setFeedbackPlatform(e.target.value)}
                        className="w-full border-2 border-black p-2 text-sm focus:outline-none cursor-pointer bg-white"
                      >
                        <option value="ikas">İKAS</option>
                        <option value="shopify">Shopify</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-bold block">ELDE EDİLEN ROAS *</label>
                      <input 
                        type="text" 
                        value={feedbackRoas}
                        onChange={(e) => setFeedbackRoas(e.target.value)}
                        placeholder="Örn: 5.4x"
                        className="w-full border-2 border-black p-2 text-sm focus:outline-none"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold block">YORUMUNUZ *</label>
                    <textarea 
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      placeholder="Reklam süreçleriniz, elde ettiğiniz ciro artışı ve iş birliğimiz..."
                      rows={3}
                      className="w-full border-2 border-black p-2 text-sm focus:outline-none resize-none"
                      required
                    />
                  </div>

                  <button 
                    type="submit"
                    className="w-full bg-[#9EFF00] text-black border-2 border-black py-3 font-bold text-sm shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none transition-all cursor-pointer"
                  >
                    Yorumu Gönder
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-8 space-y-4 animate-zoom-in">
                <div className="w-16 h-16 bg-[#9EFF00] border-4 border-black flex items-center justify-center mx-auto shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
                  <CheckCircle2 className="w-10 h-10 text-black" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-xl font-bold uppercase">Yorumunuz Alındı!</h3>
                  <p className="text-xs text-neutral-600 font-semibold max-w-xs mx-auto">
                    Geri bildiriminiz başarıyla Dijipo ekibine iletildi. İnceleme sonrasında sitemizde yayınlanacaktır. Katkınız için çok teşekkür ederiz!
                  </p>
                </div>
                <button 
                  onClick={() => { setIsFeedbackOpen(false); setIsFeedbackSubmitted(false); }}
                  className="bg-black text-white border-2 border-black px-6 py-2 font-bold text-xs shadow-[3px_3px_0px_0px_rgba(158,255,0,1)] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none transition-all"
                >
                  Kapat
                </button>
              </div>
            )}
          </div>
        </div>
      )}

    </Layout>
  );
}

// Şık ve Güvenli Kayan Marquee Marka Logosu Bileşeni
